import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PublicHeader from '../components/layout/PublicHeader';
import PoweredByAmutech from '../components/layout/PoweredByAmutech';
import PaymentIframeModal from '../components/PaymentIframeModal';
import BitPurchaseModal from '../components/BitPurchaseModal';
import ExternalIframeModal from '../components/ExternalIframeModal';
import NedarimCardModal from '../components/NedarimCardModal';
import PaymentMethodDialog from '../components/PaymentMethodDialog';
import PaymentMethodPicker from '../components/PaymentMethodPicker';
import { AmbassadorsPublicBlock, type AmbassadorItem } from '../components/AmbassadorBlock';
import { DonationToastStack, type DonationToastItem } from '../components/DonationToast';
import { fireConfetti, fireConfettiCelebration } from '../lib/confettiBurst';
// Bilingual support — see frontend/src/i18n/. resolve() picks the best
// value for the active locale (with legacy-string fallback). buildLocaleUrl
// constructs the toggle target href. getBootLocale returns the locale
// decided once at app boot from ?lang=. Direction was already set on
// document.documentElement in main.tsx based on the same locale.
import { getBootLocale, buildLocaleUrl, resolve, resolveStr } from '../i18n';
import { useTranslation } from 'react-i18next';
import { ANIMATION_KEYFRAMES, getHoverEnterStyle, getHoverLeaveStyle } from '../pages/LandingPageBuilder/animations';
import DynamicProgress from './LandingPageBuilder/components/DynamicProgress';
import RecentDonorsBlock, { donorTypoFromProps } from '../components/RecentDonorsBlock';
import DynamicChart from './LandingPageBuilder/components/DynamicChart';
import { parseVideoUrl } from './LandingPageBuilder/components/videoEmbed';
import { PannedImage } from './LandingPageBuilder/components/PannedImage';
import { featureFlags } from '../lib/featureFlags';
import { currencySymbol } from '../lib/formatUtils';
import { backgroundLayerStyle } from './LandingPageBuilder/bgPresets';
import { substituteCampaignTokens } from './LandingPageBuilder/campaignFields';
import { sanitizeInlineHtml } from './LandingPageBuilder/utils';
import { TabsBlock } from './LandingPageBuilder/TabsBlock';
import GridBlock from './LandingPageBuilder/GridBlock';
import { IconGlyph, IconBox } from './LandingPageBuilder/IconGlyph';
import { ImageCarousel } from './LandingPageBuilder/ImageCarousel';
import { isHiddenByTab, activeTabIdFor } from './LandingPageBuilder/tabsUtil';
import {
  getLabelStyle as fsLabel,
  getInputStyle as fsInput,
  getTextareaStyle as fsTextarea,
  getFieldWrapStyle as fsFieldWrap,
  getRequiredMarkStyle as fsRequired,
  CheckboxOption,
} from './LandingPageBuilder/components/formStyles';

const API_BASE = '/api';

// ─── IntersectionObserver hook ─────────────────────────────────────────
const useInView = (ref: React.RefObject<HTMLElement | null>, threshold = 0.15): boolean => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// Wrapper component that handles scroll-triggered animation
const AnimatedBlock: React.FC<{
  animationType?: string;
  animationDuration?: number;
  animationDelay?: number;
  animationTrigger?: string;
  hoverEffect?: string;
  accentColor?: string;
  style: React.CSSProperties;
  children: React.ReactNode;
  blockKey: string;
}> = ({ animationType, animationDuration, animationDelay, animationTrigger, hoverEffect, accentColor, style, children, blockKey }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const [hoverStyle, setHoverStyle] = useState<React.CSSProperties>({});

  const hasAnimation = animationType && animationType !== 'none';
  const trigger = animationTrigger || 'onScroll';
  const shouldAnimate = hasAnimation && (trigger === 'onLoad' || inView);

  const animStyle: React.CSSProperties = hasAnimation ? (shouldAnimate ? {
    animation: `anim-${animationType} ${animationDuration || 600}ms ease-out ${animationDelay || 0}ms both`,
  } : {
    opacity: 0,
  }) : {};

  const hasHover = hoverEffect && hoverEffect !== 'none';

  return (
    <div
      ref={ref}
      key={blockKey}
      style={{ ...style, ...animStyle, ...hoverStyle, transition: hasHover ? 'all 0.3s ease' : undefined }}
      onMouseEnter={hasHover ? () => setHoverStyle(getHoverEnterStyle(hoverEffect as any, accentColor)) : undefined}
      onMouseLeave={hasHover ? () => setHoverStyle(getHoverLeaveStyle(hoverEffect as any)) : undefined}
    >
      {children}
    </div>
  );
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface DonationTier {
  _id?: string;
  name: string;
  amount: number;
  description: string;
  isRecurring: boolean;
}

interface CustomField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'phone' | 'email';
  required: boolean;
  options: string[];
}

interface Ambassador {
  _id?: string;
  name: string;
  /** Optional per-locale display name. Falls back to `name` when missing. */
  nameI18n?: { he: string; en?: string };
  slug: string;
  raised: number;
}

interface PublicCampaignData {
  _id: string;
  name: string;
  description: string;
  aiDescription?: string;
  imageUrl?: string;
  // Bilingual siblings — see Phase 1 contract in backend public.ts.
  // Always optional; if undefined we use the legacy fields above.
  nameI18n?: { he: string; en?: string };
  descriptionI18n?: { he: string; en?: string };
  aiDescriptionI18n?: { he: string; en?: string };
  imageUrlI18n?: { he: string; en?: string };
  supportedLanguages?: ('he' | 'en')[];
  defaultLanguage?: 'he' | 'en';
  bilingualEnabled?: boolean;
  // Multi-currency siblings — Phase 2 (USD). Defaults preserve single-ILS
  // behavior for every legacy campaign.
  allowedCurrencies?: ('ILS' | 'USD' | 'EUR' | 'GBP')[];
  defaultCurrency?: 'ILS' | 'USD' | 'EUR' | 'GBP';
  raisedByCurrency?: Record<string, number>;
  goalByCurrency?: Record<string, number>;
  goal: number;
  raised: number;
  endDate: string;
  status: string;
  orgId: string;
  orgName: string;
  orgRegistrationNumber?: string;
  orgEmail?: string;
  orgPhone?: string;
  orgAddress?: string;
  slug: string;
  primaryColor?: string;
  donationTiers?: DonationTier[];
  gallery?: string[];
  videoUrl?: string;
  customFields?: CustomField[];
  ambassadors?: Ambassador[];
  showProgress?: boolean;
  showDonorCount?: boolean;
  showCountdown?: boolean;
  showDonationsFeed?: boolean;
  recurringEnabled?: boolean;
  section46?: boolean;
  zeroCommission?: boolean;
  ambassadorContext?: { name: string; slug: string; raised: number; nameI18n?: { he: string; en?: string } } | null;
  backgroundMusicUrl?: string;
  backgroundMusicAutoplay?: boolean;
  // External payment iframe (נדרים פלוס / jGive) — present only when the
  // campaign + org are configured for it (see backend public.ts).
  externalPayment?: { provider: 'nedarim' | 'jgive'; mode: 'replace' | 'alongside'; jgiveUrl?: string };
  nedarimIframe?: { mosadId: string; apiValid: string };
  nedarimGroupe?: string;
  // Campaign receipt text — appended after "<campaign> | " in the Nedarim
  // Groupe so it prints on Nedarim's receipt (PostNedarim card-iframe flow).
  receiptMessage?: string;
  // jGive URL surfaced for the external-payment BLOCK even when the
  // campaign-level provider setting is something else (see backend public.ts).
  jgiveIframeUrl?: string;
}

const AMOUNT_OPTIONS = [50, 100, 200, 500];
const RECURRING_MONTHS_OPTIONS = [6, 12, 24, 36];
// System-wide sentinel for an open-ended ("no limit") standing order — the
// recurring-charge job treats installmentTotal >= 999 as unlimited.
const RECURRING_UNLIMITED = 999;

const FAKE_DONORS = [
  { name: 'יוסי ת.', amount: 200, time: '3 דקות', dedication: 'לרפואת כולם 🙏' },
  { name: 'מיכל כ.', amount: 100, time: '7 דקות', dedication: 'לזכר אבא שלי' },
  { name: 'אבי ש.', amount: 500, time: '12 דקות', dedication: '' },
  { name: 'רחל ד.', amount: 50, time: '18 דקות', dedication: 'בהצלחה לכם!' },
  { name: 'דוד מ.', amount: 300, time: '25 דקות', dedication: 'עם ישראל חי 🇮🇱' },
  { name: 'שרה ל.', amount: 150, time: '31 דקות', dedication: '' },
  { name: 'נועם ב.', amount: 200, time: '45 דקות', dedication: 'כל הכבוד לעשות את זה' },
  { name: 'תמר ר.', amount: 100, time: '1 שעה', dedication: 'לרפואה שלמה' },
  { name: 'אלון ג.', amount: 1000, time: '1.5 שעות', dedication: 'מגייס משפחה וחברים' },
  { name: 'ליאת פ.', amount: 75, time: '2 שעות', dedication: '' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Nedarim Plus hosted donation page ("online") URL. Unlike the card-only
 * PostNedarim iframe, this page renders Nedarim's complete donation form —
 * amount, donor details, payment type (regular/HK) — prefilled from the
 * query string. Donations are recorded via the org's Nedarim webhook;
 * campaign attribution rides on `groupe` (nedarimGroupe / slug, already
 * mapped by the webhook) plus Param1/Param2 (campaign/ambassador keys,
 * forwarded to the callback when Nedarim supports them on this surface).
 */
function buildNedarimOnlineUrl(mosadId: string, opts: {
  groupe?: string;
  currency?: string;     // 'ILS' | 'USD'
  amount?: number;       // regular: total; recurring (HK): monthly amount
  clientName?: string;
  phone?: string;
  email?: string;
  payments?: number;     // regular only: installments — Payment param (default 1)
  /** הוראת קבע — preselects Nedarim's Keva mode (KevaDefault=1); Amount
   *  becomes the MONTHLY charge and Payment the number of months
   *  (omitted = unlimited, per Nedarim's Tashlumim spec). */
  recurring?: boolean;
  recurringMonths?: number;
  param1?: string;
  param2?: string;
} = {}): string {
  const q = new URLSearchParams();
  q.set('mosad', mosadId);
  if (opts.clientName) q.set('ClientName', opts.clientName);
  if (opts.phone) q.set('Phone', opts.phone.replace(/\D/g, ''));
  if (opts.email) q.set('Email', opts.email);
  if (opts.amount && opts.amount > 0) q.set('Amount', String(opts.amount));
  if (opts.recurring) {
    q.set('KevaDefault', '1');
    if (opts.recurringMonths && opts.recurringMonths > 0) q.set('Payment', String(opts.recurringMonths));
  } else {
    q.set('Payment', String(opts.payments || 1));
  }
  q.set('currency', opts.currency === 'USD' ? '2' : '1');
  if (opts.groupe) q.set('groupe', opts.groupe);
  if (opts.param1) q.set('Param1', opts.param1);
  if (opts.param2) q.set('Param2', opts.param2);
  // URLSearchParams encodes spaces as '+', but Nedarim's page renders the
  // '+' literally (e.g. groupe "קמפיין+בניין") — use %20 instead.
  return `https://www.matara.pro/nedarimplus/online/?${q.toString().replace(/\+/g, '%20')}`;
}

/**
 * Join first+last name defensively for donor-visible surfaces (e.g.
 * Nedarim's ClientName). Donors often type their FULL name into the
 * first-name field and then their surname again in the last-name field —
 * when firstName already ends with lastName, don't repeat it
 * ("gal orlanov" + "orlanov" → "gal orlanov", not "gal orlanov orlanov").
 */
function joinDonorFullName(firstName: string, lastName: string): string {
  const f = (firstName || '').trim().replace(/\s+/g, ' ');
  const l = (lastName || '').trim().replace(/\s+/g, ' ');
  if (!f) return l;
  if (!l) return f;
  if (f.length > l.length && f.toLowerCase().endsWith(' ' + l.toLowerCase())) return f;
  return `${f} ${l}`;
}

function getInitials(name: string): string {
  return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function avatarColor(name: string, primary: string): string {
  const colors = [primary, '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
  const idx = name.charCodeAt(0) % colors.length;
  return colors[idx];
}

function lightenHex(hex: string, amount = 0.15): string {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  const r = Math.min(255, (num >> 16) + Math.round(255 * amount));
  const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * amount));
  const b = Math.min(255, (num & 0xff) + Math.round(255 * amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ─── Bit Logo SVG ─────────────────────────────────────────────────────────────

const BitLogo: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="30" cy="30" r="30" fill="#00ADA8"/>
    <text x="30" y="38" textAnchor="middle" fill="white" fontSize="20" fontWeight="800" fontFamily="Arial, sans-serif">bit</text>
  </svg>
);

// ─── Countdown ────────────────────────────────────────────────────────────────

const Countdown: React.FC<{ endDate: string; primary: string }> = ({ endDate, primary }) => {
  const [t, setT] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const calc = useCallback(() => {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  }, [endDate]);

  useEffect(() => {
    setT(calc());
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, [calc]);

  const boxes = [
    { value: t.days, label: 'ימים' },
    { value: t.hours, label: 'שעות' },
    { value: t.minutes, label: 'דקות' },
    { value: t.seconds, label: 'שניות', pulse: true },
  ];

  return (
    <div className="flex gap-3 justify-center">
      {boxes.map(({ value, label, pulse }) => (
        <div
          key={label}
          className={`flex-1 text-center bg-white rounded-2xl py-4 px-2 relative overflow-hidden transition-all`}
          style={{
            border: `2px solid ${pulse ? primary : '#E5E7EB'}`,
            boxShadow: pulse ? `0 0 0 4px ${primary}20` : '0 2px 8px rgba(0,0,0,0.06)',
            animation: pulse ? 'pulse-border 1s ease-in-out infinite' : undefined,
          }}
        >
          <div className="text-3xl md:text-4xl font-black tabular-nums mb-1" style={{ color: primary }}>
            {String(value).padStart(2, '0')}
          </div>
          <div className="text-gray-400 text-xs font-semibold tracking-wide">{label}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Video embed ──────────────────────────────────────────────────────────────

const VideoEmbed: React.FC<{ url: string }> = ({ url }) => {
  const ytId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
  const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1];

  if (ytId) return (
    <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-lg">
      <iframe src={`https://www.youtube.com/embed/${ytId}`} title="YouTube" frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen className="w-full h-full" />
    </div>
  );
  if (vimeoId) return (
    <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-lg">
      <iframe src={`https://player.vimeo.com/video/${vimeoId}`} title="Vimeo" frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full h-full" />
    </div>
  );
  return <video src={url} controls className="w-full rounded-2xl shadow-lg max-h-80 bg-gray-900" />;
};

// ─── Gallery carousel ─────────────────────────────────────────────────────────

const GalleryCarousel: React.FC<{ images: string[]; mainImage?: string }> = ({ images, mainImage }) => {
  const all = mainImage ? [mainImage, ...images.filter(u => u !== mainImage)] : images;
  const [idx, setIdx] = useState(0);
  if (!all.length) return null;

  return (
    <div className="space-y-3">
      <div className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-lg bg-gray-100">
        <img src={all[idx]} alt="" className="w-full h-full object-cover transition-opacity duration-300" />
        {all.length > 1 && (
          <>
            <button onClick={() => setIdx(i => (i - 1 + all.length) % all.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg flex items-center justify-center text-lg font-bold transition-all hover:scale-110">‹</button>
            <button onClick={() => setIdx(i => (i + 1) % all.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg flex items-center justify-center text-lg font-bold transition-all hover:scale-110">›</button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {all.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)}
                  className={`rounded-full transition-all ${i === idx ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/50'}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {all.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {all.map((url, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === idx ? 'border-blue-500 shadow-md scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Hero Slideshow ───────────────────────────────────────────────────────────

interface HeroSlideshowProps {
  images: string[];
  children: React.ReactNode;
  minHeight?: string;
}

const HeroSlideshow: React.FC<HeroSlideshowProps> = ({ images, children, minHeight = '70vh' }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((idx: number) => {
    if (transitioning || idx === currentIdx) return;
    setPrevIdx(currentIdx);
    setCurrentIdx(idx);
    setTransitioning(true);
    setTimeout(() => {
      setPrevIdx(null);
      setTransitioning(false);
    }, 1000);
  }, [currentIdx, transitioning]);

  const next = useCallback(() => {
    goTo((currentIdx + 1) % images.length);
  }, [currentIdx, images.length, goTo]);

  const prev = useCallback(() => {
    goTo((currentIdx - 1 + images.length) % images.length);
  }, [currentIdx, images.length, goTo]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (images.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrentIdx(prev => {
        const nextIdx = (prev + 1) % images.length;
        setPrevIdx(prev);
        setTransitioning(true);
        setTimeout(() => {
          setPrevIdx(null);
          setTransitioning(false);
        }, 1000);
        return nextIdx;
      });
    }, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [images.length]);

  // Reset timer on manual navigation
  const handleManualNav = useCallback((fn: () => void) => {
    if (timerRef.current) clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(() => {
      setCurrentIdx(prev => {
        const nextIdx = (prev + 1) % images.length;
        setPrevIdx(prev);
        setTransitioning(true);
        setTimeout(() => {
          setPrevIdx(null);
          setTransitioning(false);
        }, 1000);
        return nextIdx;
      });
    }, 5000);
  }, [images.length]);

  return (
    <div className="relative w-full overflow-hidden" style={{ minHeight }}>
      {/* Previous slide (fading out) */}
      {prevIdx !== null && (
        <img
          key={`prev-${prevIdx}`}
          src={images[prevIdx]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 1, zIndex: 1 }}
        />
      )}
      {/* Current slide (fading in) */}
      <img
        key={`curr-${currentIdx}`}
        src={images[currentIdx]}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: transitioning ? 0 : 1,
          transition: 'opacity 1s ease',
          zIndex: 2,
        }}
      />

      {/* Gradient overlays */}
      <div className="absolute inset-0 hero-shimmer" style={{ zIndex: 3 }} />
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.20) 40%, rgba(0,0,0,0.80) 100%)',
        zIndex: 3,
      }} />

      {/* Content */}
      <div className="relative" style={{ zIndex: 4 }}>
        {children}
      </div>

      {/* Left/Right arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={() => handleManualNav(prev)}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white text-xl font-bold transition-all hover:scale-110"
            style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', zIndex: 5 }}
            aria-label="הקודם"
          >
            ‹
          </button>
          <button
            onClick={() => handleManualNav(next)}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white text-xl font-bold transition-all hover:scale-110"
            style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', zIndex: 5 }}
            aria-label="הבא"
          >
            ›
          </button>

          {/* Dot navigation */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2" style={{ zIndex: 5 }}>
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => handleManualNav(() => goTo(i))}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === currentIdx ? 20 : 8,
                  height: 8,
                  background: i === currentIdx ? '#fff' : 'rgba(255,255,255,0.45)',
                }}
                aria-label={`שקופית ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Custom field ─────────────────────────────────────────────────────────────

const CustomFieldInput: React.FC<{ field: CustomField; value: any; onChange: (v: any) => void }> = ({ field, value, onChange }) => {
  const base = "w-full px-4 py-3 bg-white border border-gray-200 text-gray-800 placeholder-gray-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:border-blue-400 transition-all";

  if (field.type === 'checkbox') return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${value ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}
        onClick={() => onChange(!value)}>
        {value && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
      </div>
      <span className="text-gray-700 text-sm">{field.label}{field.required && ' *'}</span>
    </label>
  );

  if (field.type === 'select') return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} required={field.required} className={base}>
      <option value="">בחר...</option>
      {field.options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const typeMap: Record<string, string> = { number: 'number', phone: 'tel', email: 'email', text: 'text' };
  return (
    <input type={typeMap[field.type] || 'text'} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={field.label} required={field.required} className={base} />
  );
};

// ─── Animated number ──────────────────────────────────────────────────────────

const AnimatedNumber: React.FC<{
  value: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ value, prefix = '', suffix = '', className = '', style }) => {
  const [display, setDisplay] = useState(0);
  const raf = useRef<number>();

  useEffect(() => {
    const start = Date.now();
    const duration = 2000;
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.round(value * eased));
      if (progress < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [value]);

  return <span className={className} style={style}>{prefix}{display.toLocaleString('he-IL')}{suffix}</span>;
};

// ─── Donor card ───────────────────────────────────────────────────────────────

const DonorCard: React.FC<{ donor: typeof FAKE_DONORS[0]; primary: string; index: number }> = ({ donor, primary, index }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), index * 80);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-start gap-3 transition-all duration-500 hover:shadow-md hover:-translate-y-0.5 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm"
        style={{ background: avatarColor(donor.name, primary) }}>
        {getInitials(donor.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-gray-800 text-sm">{donor.name}</span>
          <span className="font-black text-sm flex-shrink-0" style={{ color: primary }}>₪{donor.amount.toLocaleString()}</span>
        </div>
        {donor.dedication && <p className="text-gray-500 text-xs mt-0.5 truncate">"{donor.dedication}"</p>}
        <p className="text-gray-400 text-xs mt-0.5">לפני {donor.time}</p>
      </div>
    </div>
  );
};

// ─── Device mode hook ────────────────────────────────────────────────────────

type DeviceMode = 'desktop' | 'mobile';

const useDeviceMode = (): DeviceMode => {
  const getMode = (): DeviceMode => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop');
  const [mode, setMode] = useState<DeviceMode>(getMode);
  useEffect(() => {
    const onResize = () => setMode(getMode());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mode;
};

// ─── BuilderBlock type ───────────────────────────────────────────────────────

interface BuilderBlock {
  id: string;
  type: string;
  content?: string;
  props?: Record<string, any>;
  style?: Record<string, any>;
  mobileStyle?: Record<string, any>;
  mobileProps?: Record<string, any>;
  desktopLayout?: { x: number; y: number; width: number; height: number };
  mobileLayout?: { x: number; y: number; width: number; height: number };
  hiddenOnMobile?: boolean;
  hiddenOnDesktop?: boolean;
}

const isBlockVisibleInDevice = (block: BuilderBlock, device: DeviceMode): boolean => {
  if (device === 'mobile' && block.hiddenOnMobile) return false;
  if (device === 'desktop' && block.hiddenOnDesktop) return false;
  return true;
};

const hasNewBlocks = (campaign: any): boolean => {
  return Array.isArray(campaign.blocks) && campaign.blocks.length > 0;
};

// ─── Per-block slot ──────────────────────────────────────────────────────────
// Each block renders through its own component instance so the React hooks a
// few block types use (hero/image slideshow timers, countdown ticker, button
// hover state) live on a PER-BLOCK fiber. Previously renderBlock was invoked
// inline inside blocks.map(), which flattened every block's hooks onto the one
// CampaignBlocksRenderer fiber. When the device-mode filter dropped a block
// (e.g. a hiddenOnMobile block once the viewport shrank to mobile), the
// parent's hook count changed between renders and React threw "rendered fewer
// hooks than expected" (#311), crashing the public page. Isolating each block
// in its own fiber means hidden blocks simply unmount — no hook-order break.
const BlockSlot: React.FC<{
  block: BuilderBlock;
  deviceMode: DeviceMode;
  primary: string;
  renderBlock: (b: BuilderBlock) => React.ReactElement | null;
}> = ({ block, deviceMode, primary, renderBlock }) => {
  const rendered = renderBlock(block);
  if (!rendered) return null;
  const s = deviceMode === 'mobile' ? { ...block.style, ...block.mobileStyle } : (block.style || {});
  // Wrap with AnimatedBlock if animation is set (skip payment-button — handled internally)
  if (block.type !== 'payment-button' && ((s.animationType && s.animationType !== 'none') || (s.hoverEffect && s.hoverEffect !== 'none'))) {
    return (
      <AnimatedBlock
        blockKey={block.id}
        animationType={s.animationType}
        animationDuration={s.animationDuration}
        animationDelay={s.animationDelay}
        animationTrigger={s.animationTrigger}
        hoverEffect={s.hoverEffect}
        accentColor={primary}
        style={rendered.props.style || {}}
      >
        {rendered.props.children}
      </AnimatedBlock>
    );
  }
  return rendered;
};


// ─── Campaign Blocks Renderer ────────────────────────────────────────────────

const CampaignBlocksRenderer: React.FC<{
  campaign: any;
  primary: string;
  ambassadorRef?: string | null;
  mailCampaignId?: string | null;
}> = ({ campaign, primary, ambassadorRef, mailCampaignId }) => {
  const deviceMode = useDeviceMode();
  // i18n: every donor-facing chrome string (countdown unit labels, payment
  // checkbox text, time-ago, etc.) goes through `t()`. The locale was
  // decided at boot from ?lang= and i18next was initialized accordingly,
  // so this is a pure read — no re-renders on switch (next reload picks
  // the other language). Hebrew translations match the previous hard-coded
  // strings byte-for-byte so legacy campaigns look identical.
  const { t } = useTranslation();
  // Active locale for ALL renderer-wide consumers (page wrapper dir, footer
  // dir, per-block resolvers, DynamicProgress, ...). Computed once from the
  // boot-time URL because locale never changes mid-session.
  const rendererLocale = getBootLocale();
  // Strip blocks the designer hid on this viewport before any downstream
  // calculation (canvas height, animation wrap, etc.) so the public render
  // mirrors the editor preview for this device. When the per-device-visibility
  // flag is off, no filtering happens — every block renders in every viewport.
  //
  // MUST be memoized — many useMemo/useLayoutEffect hooks below take `blocks`
  // as a dep. A bare `.filter()` returns a new array reference on every render
  // even when content is identical, which would invalidate every downstream
  // memo and re-fire layout effects that call setBlockDeltas → infinite loop
  // (React error #185).
  const blocks: BuilderBlock[] = useMemo(() => {
    const raw: BuilderBlock[] = campaign.blocks || [];
    if (!featureFlags.perDeviceVisibility) return raw;
    return raw.filter((b: BuilderBlock) => isBlockVisibleInDevice(b, deviceMode));
  }, [campaign.blocks, deviceMode]);

  // Donation form state
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [selectedTierKey, setSelectedTierKey] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorFirstName, setDonorFirstName] = useState('');
  const [donorLastName, setDonorLastName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  // Anonymous donation: when ticked, the donor's name is hidden from the public
  // recent-donors / ambassadors lists and live toasts. The real name is still
  // submitted (income table + receipts show it as usual).
  const [donateAnonymously, setDonateAnonymously] = useState(false);
  // Multi-currency: donor-selected currency for this campaign. Defaults to the
  // campaign's defaultCurrency. Single-currency campaigns (legacy) keep this
  // pinned to 'ILS' and the toggle UI is hidden.
  const [currency, setCurrency] = useState<'ILS' | 'USD' | 'EUR' | 'GBP'>(
    ((campaign as any).defaultCurrency as any) || 'ILS'
  );
  // Default the recurring checkbox ON when the payment-button block opted in
  // (recurringDefaultOn) — but only when recurring is actually offered (the
  // toggle is shown AND recurring is enabled for this campaign), so we never
  // silently submit a monthly charge the donor can't see/uncheck.
  const [isRecurring, setIsRecurring] = useState<boolean>(() => {
    const pay = (campaign.blocks || []).find((b: any) => b?.type === 'payment-button');
    const pp: any = pay?.props || {};
    return !!pp.recurringDefaultOn && pp.showRecurringToggle !== false && campaign.recurringEnabled !== false;
  });
  const [recurringMonths, setRecurringMonths] = useState(12);
  // Day-of-month (1..28) for the recurring charge. Defaults to today's day,
  // capped at 28 so the chosen day exists in every month. First charge runs
  // immediately; subsequent charges land on this day each following month.
  const [chargingDay, setChargingDay] = useState<number>(() => Math.min(28, new Date().getDate()));
  // Ambassador roster (fetched once when the campaign loads if any
  // ambassadors-public block exists in the layout). Re-fetched on focus so
  // donors who just contributed see their amount reflected.
  const [ambassadorsPublic, setAmbassadorsPublic] = useState<Array<{
    name: string; slug: string; goal: number; raised: number;
    donorsCount: number; recentDonors: Array<{ name: string; amount: number; createdAt: string }>;
  }>>([]);
  const hasAmbassadorsBlock = useMemo(
    () => (blocks || []).some((b: any) => b?.type === 'ambassadors-public'),
    [blocks]
  );
  useEffect(() => {
    if (!hasAmbassadorsBlock || !campaign?.slug) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API_BASE}/public/campaigns/${campaign.slug}/ambassadors-public`);
        if (!cancelled) setAmbassadorsPublic(r.data?.ambassadors || []);
      } catch { /* renderer falls back to whatever the block had on load */ }
    };
    load();
    // Refresh when the tab regains focus so the user sees their own donation
    // reflected if they came back from the payment confirmation page.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, [hasAmbassadorsBlock, campaign?.slug]);

  // ─── Live donation toasts ───────────────────────────────────────────────
  // When the campaign owner enabled `showDonationToasts`, poll the public
  // endpoint and pop a toast on the bottom-left for each newly-completed
  // donation. No real-time channel exists yet — polling the same snapshot the
  // page already uses is the lightest path. Everything present at first load
  // is seeded into `seen` so we never replay donation history as toasts.
  const showDonationToasts = !!(campaign as any).showDonationToasts;
  // Live progress meter (count-up) + confetti on new donations. Both opt-in.
  const liveMeter = !!(campaign as any).liveMeter;
  const donationConfetti = !!(campaign as any).donationConfetti;
  // Goal-reached celebration: a sustained 2-minute confetti rain the moment the
  // campaign crosses its goal live. On by default unless explicitly disabled.
  const goalConfetti = (campaign as any).goalConfetti !== false;
  const [donationToasts, setDonationToasts] = useState<DonationToastItem[]>([]);
  // Live-updating raised totals, seeded from the loaded campaign and refreshed
  // by the same poll. The meter reads these (when liveMeter is on) so the
  // amount rolls up to each new total without a reload.
  const [liveStats, setLiveStats] = useState({
    raised: (campaign as any).raised ?? 0,
    raisedByCurrency: (campaign as any).raisedByCurrency,
    recurringCount: (campaign as any).recurringCount ?? 0,
    donationCount: (campaign as any).donationCount ?? 0,
  });
  // Latest recent-donors list from the poll. Null until the first tick lands;
  // the recent-donors block falls back to the load-time snapshot until then.
  // Consumed whenever any live feature is on (see the block renderer).
  const [liveRecentDonors, setLiveRecentDonors] = useState<any[] | null>(null);
  const seenDonorsRef = useRef<Set<string> | null>(null);
  const donorKey = (d: any) => `${d.date || d.createdAt || ''}|${d.name || ''}|${d.amount || 0}`;
  // Poll whenever any live feature is on — toasts, the live meter, or confetti
  // all read the same snapshot, so a single poll feeds all three.
  const livePollEnabled = showDonationToasts || liveMeter || donationConfetti || goalConfetti;
  // Tracks the last-seen raised total + whether we've already celebrated, so the
  // goal celebration fires exactly once on the live crossing (not on every poll,
  // and not on load when the campaign is already at/over goal).
  const prevRaisedRef = useRef<number | null>(null);
  const goalCelebratedRef = useRef(false);
  useEffect(() => {
    if (!livePollEnabled || !campaign?.slug) return;
    // Seed the baseline once: existing donors are "already seen" — no replay.
    if (seenDonorsRef.current === null) {
      seenDonorsRef.current = new Set<string>((campaign.recentDonors || []).map(donorKey));
    }
    if (prevRaisedRef.current === null) prevRaisedRef.current = (campaign as any).raised ?? 0;
    const symbol = currencySymbol((campaign as any).defaultCurrency || 'ILS');
    let cancelled = false;
    const poll = async () => {
      // Don't poll a backgrounded tab — saves the server a request every 8s for
      // each open-but-hidden tab. We refresh immediately on becoming visible.
      if (document.hidden) return;
      try {
        // Lightweight live snapshot — only the changing numbers + recent donors,
        // not the whole campaign payload (blocks/org/ambassadors). Same response
        // shape for the fields we read, so the meter + toast de-dup are unchanged.
        const r = await axios.get(`${API_BASE}/public/campaigns/${campaign.slug}/live`);
        if (cancelled) return;
        // Refresh the live totals first so the meter counts up to the new
        // amount even when the new donation isn't in the recent-donors top-10.
        const newRaised = r.data?.raised ?? 0;
        setLiveStats({
          raised: newRaised,
          raisedByCurrency: r.data?.raisedByCurrency,
          recurringCount: r.data?.recurringCount ?? 0,
          donationCount: r.data?.donationCount ?? 0,
        });
        // Goal celebration: fire once when raised crosses the goal live. Compare
        // against the previous total so a campaign that's already at goal on load
        // doesn't trigger it — only an actual crossing during this session does.
        const goal = Number(r.data?.goal ?? (campaign as any).goal) || 0;
        const prevRaised = prevRaisedRef.current ?? newRaised;
        if (goalConfetti && goal > 0 && !goalCelebratedRef.current && prevRaised < goal && newRaised >= goal) {
          goalCelebratedRef.current = true;
          fireConfettiCelebration();
        }
        prevRaisedRef.current = newRaised;
        const donors: any[] = r.data?.recentDonors || [];
        // Feed the recent-donors block so it reflects new donations live (the
        // block reads this whenever any live feature is on). Set unconditionally
        // every tick — even when no donation is "fresh", names/amounts may have
        // shifted on the server.
        setLiveRecentDonors(donors);
        const seen = seenDonorsRef.current!;
        // Endpoint returns newest-first; reverse so multiple new donations
        // queue oldest→newest and read naturally in the stack.
        const fresh = donors.filter((d) => !seen.has(donorKey(d))).reverse();
        if (!fresh.length) return;
        fresh.forEach((d) => seen.add(donorKey(d)));
        // Confetti on any newly-seen donation (once per tick, the util itself
        // ignores re-entrant calls).
        if (donationConfetti) fireConfetti();
        if (showDonationToasts) {
          const next: DonationToastItem[] = fresh.map((d) => {
            const ambassadorName = resolveStr(d.ambassadorNameI18n, rendererLocale) || d.ambassadorName || '';
            return {
              key: donorKey(d),
              name: (d.name || '').trim() || t('public.recentDonors.anonymousDonor'),
              amount: d.amount || 0,
              symbol,
              ambassadorName,
            };
          });
          // Keep at most the 5 most-recent toasts on screen — a burst of
          // donations in one tick won't flood the corner.
          setDonationToasts((prev) => [...prev, ...next].slice(-5));
        }
      } catch { /* transient network error — next tick retries */ }
    };
    const id = window.setInterval(poll, 8000);
    const onFocus = () => poll();
    // Poll the moment the tab returns to the foreground, so a returning user
    // sees fresh totals without waiting for the next interval tick.
    const onVisible = () => { if (!document.hidden) poll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; window.clearInterval(id); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePollEnabled, showDonationToasts, donationConfetti, goalConfetti, campaign?.slug]);
  const dismissToast = useCallback((key: string) => {
    setDonationToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const [recurringConsent, setRecurringConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [donationError, setDonationError] = useState<string | null>(null);
  const [donationSuccess, setDonationSuccess] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  // Dedicated "pay with bit" button (optional, per payment-button block):
  // probe whether the org has BIT configured, and open the modal straight
  // into the BIT flow when the donor taps it.
  const [bitButtonAvailable, setBitButtonAvailable] = useState(false);
  const [bitDirect, setBitDirect] = useState(false);
  // Native payment-method picker (campaign.showPaymentMethodPicker). When open,
  // the donor chose to pay and picks bit / card / Apple / Google in the dialog.
  const [pickerOpen, setPickerOpen] = useState(false);
  // Tranzila-only gate for the picker — set from /providers-available (wallet).
  // Hyp campaigns stay false, so the picker never opens for them.
  const [walletPickerEligible, setWalletPickerEligible] = useState(false);
  // External payment iframes (נדרים פלוס / jGive) — configured per campaign in
  // the builder; donations land via the provider's webhook, not our form.
  const [nedarimModalOpen, setNedarimModalOpen] = useState(false);
  const [externalJgiveOpen, setExternalJgiveOpen] = useState(false);
  // external-payment block in 'modal' display mode — the block renders just a
  // button; clicking it opens the payment surface (Nedarim's hosted online
  // page / jGive / manual customUrl) in an iframe popup.
  const [extBlockModal, setExtBlockModal] = useState<{ url: string; title: string } | null>(null);

  // Some blocks render at a height that depends on live data (donor count,
  // recurring-panel toggle, etc.) and don't match the height the designer
  // authored in the editor. We measure each one's actual rendered height and
  // shift sibling blocks below it by the delta (delta can be negative — e.g.
  // an ambassadors block authored for 4 cards but only 1 ambassador exists
  // should shrink and pull content beneath it up).
  // The public canvas is the SAME fixed pixel width (800 desktop / 390
  // mobile) as the editor and is scaled via `zoom: ratio`, so in THEORY a
  // text block wraps to the same number of lines everywhere. In practice iOS
  // Safari broke that: its default `-webkit-text-size-adjust: auto` inflates
  // text font-size by the real viewport (not the zoomed canvas), so the same
  // block wrapped to different line counts per device — underfilling its box
  // and opening gaps before the next block. The real fix is pinning
  // text-size-adjust: 100% on the canvas (see the render below), which makes
  // every device wrap like the editor. text/heading/donation-tiers are kept
  // in the dynamic+shrink sets as a belt-and-suspenders: once wrapping is
  // identical their measured delta is ~0 (no-op), but if any residual height
  // difference remains they shrink to content and pull siblings up to the
  // authored gap rather than leaving dead space.
  //
  // Kept dynamic:
  // - payment-button: toggling the recurring panel adds real height at runtime
  // - ambassadors-public, recent-donors: variable item count loaded async
  // - button: when a label wraps to more lines than fit the authored
  //   height (common on narrow mobile widths where a desktop-sized button
  //   becomes 2 lines), the wrapper grows past authored height. Shifting
  //   siblings keeps the next block from overlapping the grown button.
  const DYNAMIC_HEIGHT_TYPES = useMemo(() => new Set(['payment-button', 'ambassadors-public', 'recent-donors', 'button', 'text', 'heading', 'donation-tiers']), []);
  // Blocks allowed to render SHORTER than their authored height, sizing to
  // their real content and pulling siblings up by the slack.
  // - ambassadors/recent-donors: variable item count is the whole reason
  //   they're dynamic.
  // - text/heading: the authored height was measured in the 390px builder
  //   canvas, but real devices wrap the copy to a different number of lines
  //   (font metrics, effective width). Pinning the box to the authored
  //   height left a dead gap before the next block whenever the live text
  //   wrapped shorter. Letting them shrink keeps the gap below equal to the
  //   AUTHORED gap (see offsetContribution) on every device — matching the
  //   builder 1:1. They still grow + shift siblings when copy wraps longer.
  // - donation-tiers: same as text — the authored box was measured in the
  //   builder, but the tier buttons render at their own intrinsic height on
  //   the device, so pinning left a gap before the next block. Let it size to
  //   the buttons and keep the authored gap below.
  // - payment-button: its authored box is often taller than the live button
  //   (recurring panel collapsed, amount input hidden, etc.); without shrink
  //   the leftover space stays inside the box and the next block is stranded
  //   below it. Shrinking + the offsetContribution change pulls siblings up.
  const SHRINK_ENABLED_TYPES = useMemo(() => new Set(['ambassadors-public', 'recent-donors', 'text', 'heading', 'donation-tiers', 'payment-button']), []);
  // Visual breathing-room added on top of POSITIVE overflow of the payment
  // block so the pushed-down content isn't pressed flush against the
  // recurring panel.
  const RECURRING_GAP = 32;

  const dynamicBlockRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [blockDeltas, setBlockDeltas] = useState<Record<string, number>>({});

  const paymentBlock = useMemo(() => blocks.find(b => b.type === 'payment-button'), [blocks]);
  const paymentBlockRef = useCallback((el: HTMLDivElement | null) => {
    if (paymentBlock) dynamicBlockRefs.current[paymentBlock.id] = el;
  }, [paymentBlock]);

  const measureDynamicBlock = useCallback((blockId: string) => {
    const el = dynamicBlockRefs.current[blockId];
    const block = blocks.find(b => b.id === blockId);
    if (!el || !block) return;
    const layout = deviceMode === 'desktop' ? block.desktopLayout : block.mobileLayout;
    if (!layout) return;
    // Round to nearest 4px — sub-pixel jitter from font rendering, ResizeObserver
    // micro-fires during mobile scroll, etc. would otherwise flip state on
    // every paint and starve the main thread mid-scroll, making the page
    // feel "stuck" on iOS Safari.
    const measured = el.offsetHeight;
    const delta = Math.round((measured - layout.height) / 4) * 4;
    setBlockDeltas(prev => (prev[blockId] === delta ? prev : { ...prev, [blockId]: delta }));
  }, [blocks, deviceMode]);

  const dynamicBlocks = useMemo(
    () => blocks.filter(b => DYNAMIC_HEIGHT_TYPES.has(b.type)),
    [blocks, DYNAMIC_HEIGHT_TYPES],
  );

  // For each block, the authored distance from its bottom edge to the
  // nearest authored block below it — i.e. the empty space the designer
  // left underneath. When a dynamic block grows, content first absorbs
  // this gap before we push siblings down (matches the editor preview,
  // which lets content overflow into the gap without shifting anything).
  const blockGapsBelow = useMemo(() => {
    const map = new Map<string, number>();
    for (const block of blocks) {
      const layout = deviceMode === 'desktop' ? block.desktopLayout : block.mobileLayout;
      if (!layout) continue;
      const myBottom = layout.y + layout.height;
      let minGap = Infinity;
      for (const other of blocks) {
        if (other.id === block.id) continue;
        // The background block fills the whole canvas (its stored layout is
        // ignored at render — see the `layout` override below). A designer
        // can leave its authored y anywhere (e.g. 420), so counting it here
        // would make it look like the "nearest block below" and produce a
        // falsely-small authored gap. That under-absorbs on grow and pushes
        // every following block down by the difference, opening a gap on the
        // live page that the editor never shows. Skip it — only real flow
        // blocks define the empty space below.
        if (other.type === 'background') continue;
        const otherLayout = deviceMode === 'desktop' ? other.desktopLayout : other.mobileLayout;
        if (!otherLayout) continue;
        if (otherLayout.y < myBottom) continue; // not below
        const gap = otherLayout.y - myBottom;
        if (gap < minGap) minGap = gap;
      }
      map.set(block.id, minGap === Infinity ? 0 : minGap);
    }
    return map;
  }, [blocks, deviceMode]);

  // Translate a measured delta into the actual offset applied to siblings
  // below.
  // - payment-button: now behaves like every other block — SHRINK pulls
  //   siblings up by the full delta; GROW first absorbs the authored gap
  //   below, only shifting siblings by the overflow that EXCEEDS the gap.
  //   The +RECURRING_GAP breathing room is added ONLY while the recurring
  //   consent panel is expanded (interactive content that shouldn't touch
  //   the block below). Previously GROW always added the gap and never
  //   absorbed the authored gap, so the next block sat ~102px below the
  //   button even with the panel collapsed — a dead gap the designer never
  //   authored (their authored gap here was 70px).
  // - everything else: shrink pulls siblings up by the full delta; grow
  //   first absorbs the authored gap below (so visual spacing matches the
  //   preview), and only shifts siblings by the AMOUNT THAT EXCEEDS the
  //   gap (so content still doesn't overlap the next block).
  const offsetContribution = (block: BuilderBlock, delta: number): number => {
    if (delta <= 0) return delta;
    const gap = blockGapsBelow.get(block.id) ?? 0;
    const base = Math.max(0, delta - gap);
    if (block.type === 'payment-button') return base + (isRecurring ? RECURRING_GAP : 0);
    return base;
  };

  // Sum of offset contributions from every dynamic block whose authored top
  // is above (or equal to) this block's top — i.e. every block that, if it
  // grows or shrinks, drags this one along with it.
  const getCumulativeOffset = useCallback((targetY: number, excludeBlockId?: string): number => {
    let offset = 0;
    for (const db of dynamicBlocks) {
      if (db.id === excludeBlockId) continue;
      const dbLayout = deviceMode === 'desktop' ? db.desktopLayout : db.mobileLayout;
      if (!dbLayout) continue;
      if (dbLayout.y > targetY) continue; // strictly below — doesn't affect us
      const delta = blockDeltas[db.id] ?? 0;
      offset += offsetContribution(db, delta);
    }
    return offset;
  }, [dynamicBlocks, deviceMode, blockDeltas, blockGapsBelow, isRecurring]);

  // Re-measure synchronously after DOM updates we know change height
  // (recurring panel toggle, donor data arriving) so siblings reflow on the
  // same paint and the page doesn't flash.
  useLayoutEffect(() => {
    for (const db of dynamicBlocks) measureDynamicBlock(db.id);
  }, [dynamicBlocks, measureDynamicBlock, isRecurring, recurringConsent, ambassadorsPublic, campaign?.recentDonors]);

  // Observe runtime growth (text wrapping, font loading, async data, etc.).
  useEffect(() => {
    const observers: ResizeObserver[] = [];
    for (const db of dynamicBlocks) {
      const el = dynamicBlockRefs.current[db.id];
      if (!el) continue;
      const ro = new ResizeObserver(() => measureDynamicBlock(db.id));
      ro.observe(el);
      observers.push(ro);
    }
    return () => { for (const ro of observers) ro.disconnect(); };
  }, [dynamicBlocks, measureDynamicBlock]);

  const getAmount = () => customAmount ? parseFloat(customAmount) : (selectedTier || 0);

  const getFullName = () => {
    const split = [donorFirstName, donorLastName].filter(s => s && s.trim()).join(' ').trim();
    return split || donorName.trim();
  };

  // Returns first/last name suitable for HYP's separate ClientName/ClientLName.
  // If the campaign has separate fields → use them directly.
  // If only the combined "full name" field is filled → split on the first space.
  const getNameParts = () => {
    if (donorFirstName.trim() || donorLastName.trim()) {
      return { firstName: donorFirstName.trim(), lastName: donorLastName.trim() };
    }
    const full = donorName.trim();
    if (!full) return { firstName: '', lastName: '' };
    const idx = full.indexOf(' ');
    if (idx === -1) return { firstName: full, lastName: '' };
    return { firstName: full.slice(0, idx), lastName: full.slice(idx + 1).trim() };
  };

  const bitContext = useMemo(() => {
    if (!campaign?.orgId) return undefined;
    const amount = customAmount ? parseFloat(customAmount) : (selectedTier || 0);
    const split = [donorFirstName, donorLastName].filter(s => s && s.trim()).join(' ').trim();
    const fullName = split || donorName.trim();
    return {
      orgId: campaign.orgId,
      amount,
      campaignId: campaign?._id || undefined,
      ambassadorSlug: ambassadorRef || undefined,
      donorName: fullName,
      donorEmail: donorEmail || undefined,
      donorPhone: donorPhone || undefined,
      mailCampaignId: mailCampaignId || undefined,
      // Recurring donations can't go through BIT (no stored token for the
      // monthly charge) — the modal hides the "שלם ב-BIT" toggle when set.
      recurring: isRecurring,
      // Custom-field answers so the Bit flow persists them on the Donation,
      // matching the card iframe (which sends them via /public/payment-url).
      customFields: Object.keys(customFieldValues).length ? customFieldValues : undefined,
      // Anonymous donation — mirrors the card flow's `anonymous` param.
      anonymous: donateAnonymously,
    };
  }, [
    campaign?.orgId,
    campaign?._id,
    ambassadorRef,
    customAmount,
    selectedTier,
    donorFirstName,
    donorLastName,
    donorName,
    donorEmail,
    donorPhone,
    mailCampaignId,
    customFieldValues,
    isRecurring,
    donateAnonymously,
  ]);

  // Probe BIT availability for the dedicated bit button — only when a
  // payment-button block opted in (props.showBitButton), to avoid a needless
  // request on campaigns that don't use it.
  useEffect(() => {
    const wantsBit = (campaign?.blocks || []).some(
      (b: any) => b?.type === 'payment-button' && b?.props?.showBitButton,
    ) || (campaign?.showPaymentMethodPicker && campaign?.pickerEnableBit !== false);
    const wantsProbe = wantsBit || !!campaign?.showPaymentMethodPicker;
    if (!campaign?.orgId || !wantsProbe) { setBitButtonAvailable(false); setWalletPickerEligible(false); return; }
    let alive = true;
    axios.get(`${API_BASE}/payments/providers-available`, { params: { orgId: campaign.orgId, ...(campaign?._id ? { campaignId: campaign._id } : {}) } })
      .then(r => {
        if (!alive) return;
        setBitButtonAvailable(!!r.data?.bit);
        // Wallet eligibility is Tranzila-only (the backend gates it strictly).
        // Hyp terminals resolve wallet:false → the picker never opens and the
        // page keeps its existing iframe flow. Hyp is never touched.
        setWalletPickerEligible(!!r.data?.wallet);
      })
      .catch(() => { if (alive) { setBitButtonAvailable(false); setWalletPickerEligible(false); } });
    return () => { alive = false; };
  }, [campaign?.orgId, campaign?.blocks, campaign?.showPaymentMethodPicker, campaign?._id]);

  // Open the configured external payment surface (returns false when the
  // campaign has none / the org config is missing → built-in form is used).
  const openExternalPayment = (): boolean => {
    const ext = campaign?.externalPayment;
    if (!ext?.provider) return false;
    if (ext.provider === 'nedarim' && campaign?.nedarimIframe?.mosadId) {
      setNedarimModalOpen(true);
      return true;
    }
    if (ext.provider === 'jgive' && ext.jgiveUrl) {
      setExternalJgiveOpen(true);
      return true;
    }
    return false;
  };

  // Dedicated "pay with bit" button: validate the same fields as a card
  // donation (plus a mobile phone, required for the BIT SMS) and open the
  // payment modal straight into the BIT flow.
  const handleBitDonate = () => {
    const amount = getAmount();
    if (!amount || amount <= 0) { setDonationError(t('public.errors.enterAmount')); return; }
    if (!getFullName()) { setDonationError(t('public.errors.enterName')); return; }
    if (!termsAccepted) { setDonationError(t('public.errors.acceptTerms')); return; }
    if (!donorPhone || !donorPhone.trim()) { setDonationError('נא להזין טלפון נייד לתשלום ב-BIT'); return; }
    setDonationError(null);
    setPaymentUrl(null);     // BIT flow doesn't use the card iframe
    setBitDirect(true);
    setPaymentModalOpen(true);
  };

  // Native payment-method picker: validate the same fields as a card donation
  // and open the chooser. Recurring donations skip the picker — they need the
  // iframe/token flow (wallets + bit are one-off only), so they fall through to
  // handleDonate. Only reached when campaign.showPaymentMethodPicker is on.
  const openPicker = () => {
    const amount = getAmount();
    if (!amount || amount <= 0) { setDonationError(t('public.errors.enterAmount')); return; }
    if (!getFullName()) { setDonationError(t('public.errors.enterName')); return; }
    if (!termsAccepted) { setDonationError(t('public.errors.acceptTerms')); return; }
    setDonationError(null);
    setPickerOpen(true);
  };

  // Fetch the Hosted Fields thtk + charge params for a one-off Apple/Google
  // wallet charge. Mirrors handleDonate's donor params (minus recurring); the
  // returned params carry notify_url + order_id so the wallet charge lands a
  // real Donation via /payments/tranzila/callback.
  const requestWalletCharge = async (_method: 'google' | 'apple') => {
    const { firstName, lastName } = getNameParts();
    const fullName = getFullName();
    const params = new URLSearchParams({
      amount: String(getAmount()),
      orgId: campaign?.orgId || '',
      campaignId: campaign?._id || '',
      clientName: firstName || fullName,
      ...(firstName && lastName ? { clientLastName: lastName } : {}),
      clientEmail: donorEmail || '',
      clientPhone: donorPhone || '',
      ...(ambassadorRef ? { ambassadorSlug: ambassadorRef } : {}),
      ...(mailCampaignId ? { mailCampaignId } : {}),
      ...(donateAnonymously ? { anonymous: 'true' } : {}),
      ...(currency && currency !== 'ILS' ? { currency } : {}),
      lang: getBootLocale(),
      ...(Object.keys(customFieldValues).length > 0 ? { customFields: JSON.stringify(customFieldValues) } : {}),
    });
    try {
      sessionStorage.setItem('lastCampaignSlug', campaign?.slug || '');
      if (ambassadorRef) sessionStorage.setItem('lastAmbassadorRef', ambassadorRef);
      else sessionStorage.removeItem('lastAmbassadorRef');
      if (Object.keys(customFieldValues).length > 0) {
        sessionStorage.setItem('pendingCustomFields', JSON.stringify(customFieldValues));
      }
    } catch {}
    const { data } = await axios.get(`${API_BASE}/public/wallet/charge-params?${params}`);
    return data as { ok: boolean; thtk?: string; terminalName?: string; chargeParams?: Record<string, string>; note?: string };
  };

  const handleDonate = async () => {
    const amount = getAmount();
    if (!amount || amount <= 0) { setDonationError(t('public.errors.enterAmount')); return; }
    const fullName = getFullName();
    if (!fullName) { setDonationError(t('public.errors.enterName')); return; }
    if (isRecurring && !recurringConsent) {
      setDonationError(t('public.payment.recurringConsentRequired'));
      return;
    }
    if (!termsAccepted) {
      setDonationError(t('public.errors.acceptTerms'));
      return;
    }
    setDonationError(null);
    // External payment iframe replaces the built-in Tranzila flow (mode
    // 'alongside' keeps this button on Tranzila and adds a secondary one).
    if (campaign?.externalPayment?.mode !== 'alongside' && openExternalPayment()) return;
    setPaymentLoading(true);
    try {
      const { firstName, lastName } = getNameParts();
      const params = new URLSearchParams({
        amount: String(amount),
        orgId: campaign.orgId || '',
        campaignId: campaign._id || '',
        clientName: firstName || fullName,
        // Only send a separate last name when we actually have a distinct
        // first name. Otherwise `clientName` already holds the full name and
        // re-sending the last name made the backend build "<last> <last>"
        // (the org renamed "שם"→"שם ההורה", emptying donorFirstName).
        ...(firstName && lastName ? { clientLastName: lastName } : {}),
        clientEmail: donorEmail || '',
        clientPhone: donorPhone || '',
        redirect: 'false',
        ...(isRecurring ? { recurring: 'true', recurringMonths: String(recurringMonths), chargingDay: String(chargingDay) } : {}),
        ...(ambassadorRef ? { ambassadorSlug: ambassadorRef } : {}),
        ...(mailCampaignId ? { mailCampaignId } : {}),
        // Anonymous donation — backend masks the donor name on the public lists.
        ...(donateAnonymously ? { anonymous: 'true' } : {}),
        // Multi-currency: forward the donor's currency only when it's not the
        // default 'ILS'. Omitting the param for ILS keeps every legacy backend
        // call site byte-identical (the route defaults to 'ILS' when absent).
        ...(currency && currency !== 'ILS' ? { currency } : {}),
        // Forward the donor's chosen locale (Hebrew by default) so the
        // backend can flip the Tranzila iframe (lang=il/en) and the issued
        // invoice (Ilang=HEB/ENG) to match. Omitted/he ⇒ identical to
        // pre-bilingual behavior.
        lang: getBootLocale(),
        // Forward the donor's custom-field answers (checkbox-group, radio,
        // select, free-text) so the backend can render {שדה.LABEL}
        // placeholders inside the receiptMessage with real values before
        // the iframe URL is built. Until this was added, the iframe path
        // had no donor answers at URL-build time and those placeholders
        // collapsed to empty strings on the auto-issued receipt.
        ...(Object.keys(customFieldValues).length > 0
          ? { customFields: JSON.stringify(customFieldValues) }
          : {}),
      });
      const { data } = await axios.get(`${API_BASE}/public/payment-url?${params}`);
      if (data.url) {
        try {
          sessionStorage.setItem('lastCampaignSlug', campaign?.slug || '');
          // Remember the ambassador so the thank-you page's "back to campaign"
          // link keeps the ?ref= — otherwise a follow-up donation loses credit.
          if (ambassadorRef) sessionStorage.setItem('lastAmbassadorRef', ambassadorRef);
          else sessionStorage.removeItem('lastAmbassadorRef');
          if (Object.keys(customFieldValues).length > 0) {
            sessionStorage.setItem('pendingCustomFields', JSON.stringify(customFieldValues));
          }
        } catch {}
        setPaymentUrl(data.url);
        setPaymentModalOpen(true);
      } else {
        setDonationError(t('public.errors.createPaymentLinkFailed'));
      }
    } catch (err: any) {
      setDonationError(err?.response?.data?.error || t('public.errors.createPaymentFailed'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSuccess = (payload: Record<string, string>) => {
    setPaymentModalOpen(false);
    setDonationSuccess(true);
    const qs = new URLSearchParams({ ...payload, campaignSlug: campaign?.slug || '' }).toString();
    window.location.href = `/payments/complete?${qs}`;
  };

  const handlePaymentFail = (payload: Record<string, string>) => {
    setPaymentModalOpen(false);
    setDonationError(payload?.error || t('public.errors.paymentNotCompleted'));
  };

  // Canvas dimensions from editor
  const canvasWidth = deviceMode === 'desktop' ? 800 : 390;
  let canvasHeight = 600;
  for (const b of blocks) {
    // Background blocks auto-fill the canvas — they don't drive height.
    if (b.type === 'background') continue;
    const layout = deviceMode === 'desktop' ? b.desktopLayout : b.mobileLayout;
    if (!layout) continue;
    // Siblings: pushed by every dynamic block authored above them.
    // Dynamic blocks themselves: pushed by every OTHER dynamic block above
    // them, plus their own measured delta (so the canvas grows to fit).
    // Shrink-enabled blocks contribute their FULL delta (incl. negative)
    // so the canvas tightens when content is shorter than authored. Other
    // dynamic blocks (text, heading, donation-tiers) keep the authored
    // floor — they grow but never shrink, so only positive delta counts.
    const offset = getCumulativeOffset(layout.y, b.id);
    const rawDelta = blockDeltas[b.id] ?? 0;
    const ownDelta = SHRINK_ENABLED_TYPES.has(b.type)
      ? rawDelta
      : (DYNAMIC_HEIGHT_TYPES.has(b.type) ? Math.max(0, rawDelta) : 0);
    canvasHeight = Math.max(canvasHeight, layout.y + offset + layout.height + ownDelta + 20);
  }

  // Active tab per tabs-block (tabsBlockId → tabId). Drives which member
  // blocks render; defaults to each tabs block's first tab.
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});

  // Track container width for proportional vertical scaling
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(canvasWidth);
  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    update();
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => { window.removeEventListener('resize', update); ro.disconnect(); };
  }, []);
  // ---- Wide-screen layout (optional, configured on the background block) ----
  // desktopMaxWidth caps the content column on desktop so the 800px canvas
  // stops zooming to fill huge monitors. bgFullBleed decides whether the
  // background paints edge-to-edge behind the side margins or stays inside the
  // column (with pageColor filling the margins). Unset ⇒ legacy full-bleed
  // behaviour (column == viewport), so existing campaigns are byte-identical.
  const bgBlockForLayout = blocks.find(b => b.type === 'background');
  const bgLayoutStyle: Record<string, any> = bgBlockForLayout
    ? (deviceMode === 'mobile' ? { ...bgBlockForLayout.style, ...bgBlockForLayout.mobileStyle } : (bgBlockForLayout.style || {}))
    : {};
  const bgLayoutProps: Record<string, any> = bgBlockForLayout
    ? (deviceMode === 'mobile' ? { ...bgBlockForLayout.props, ...bgBlockForLayout.mobileProps } : (bgBlockForLayout.props || {}))
    : {};
  const desktopMaxW = deviceMode === 'desktop' ? (Number(bgLayoutProps.desktopMaxWidth) || 0) : 0;
  const bgFullBleed = !!bgLayoutProps.bgFullBleed;
  const pageColor = bgLayoutProps.pageColor || '#e2e8f0';
  const renderWidth = desktopMaxW ? Math.min(containerWidth, desktopMaxW) : containerWidth;
  const ratio = renderWidth / canvasWidth; // scale factor for image aspect ratio

  // 'chart' stays excluded — Recharts ResponsiveContainer needs an explicit
  // pixel height from the block layout. The remaining autoHeight blocks that
  // aren't in DYNAMIC_HEIGHT_TYPES (donation-tiers, form, social, etc.) ONLY
  // grow visually and DO NOT shift siblings, so we keep a minHeight floor at
  // the authored height. The DYNAMIC_HEIGHT_TYPES set (payment-button,
  // ambassadors-public, recent-donors, button, text, heading) measures actual
  // height and shifts siblings up/down by the delta; the shrink-enabled
  // members are also allowed to render below the authored height.
  const autoHeightTypes = new Set(['donation-tiers', 'payment-button', 'recent-donors', 'ambassadors-public', 'heading', 'text', 'form', 'social', 'progress', 'button', 'external-payment']);

  // For each container, list the blocks the designer placed visually inside
  // it (authored bounding box roughly contained by the container's authored
  // box — small horizontal overhangs are tolerated). The public view treats
  // those as the container's children: the container grows in the rendered
  // page to wrap them, even if their effective heights (after dynamic
  // measurements / cumulative offsets) exceed the container's authored
  // height. This keeps the visual relationship the designer set up in the
  // editor — "this block sits on the container" — intact in production.
  //
  // CONTAIN_TOL: pixels of slack allowed on each edge. Designers routinely
  // overshoot a container's bounds by a few px when dragging blocks (e.g. a
  // heading authored at 644px wide inside a 639px-wide container — visually
  // it sits inside, but a strict-containment check would exclude it and
  // leave it dangling outside the container in production).
  const containerChildren = useMemo(() => {
    const CONTAIN_TOL = 20;
    const map = new Map<string, string[]>();
    const containers = blocks.filter(b => b.type === 'container');
    if (!containers.length) return map;
    for (const c of containers) {
      const outer = deviceMode === 'desktop' ? c.desktopLayout : c.mobileLayout;
      if (!outer) { map.set(c.id, []); continue; }
      const ids: string[] = [];
      for (const b of blocks) {
        if (b.id === c.id) continue;
        if (b.type === 'background' || b.type === 'container') continue;
        const inner = deviceMode === 'desktop' ? b.desktopLayout : b.mobileLayout;
        if (!inner) continue;
        // Inner block's center must be inside the container vertically AND
        // there must be significant horizontal overlap. Allows the inner to
        // extend up to CONTAIN_TOL past any edge of the container.
        const innerCenterY = inner.y + inner.height / 2;
        const insideVertically =
          innerCenterY >= outer.y - CONTAIN_TOL &&
          innerCenterY <= outer.y + outer.height + CONTAIN_TOL &&
          inner.y >= outer.y - CONTAIN_TOL &&
          inner.y + inner.height <= outer.y + outer.height + CONTAIN_TOL;
        const hOverlap = Math.min(inner.x + inner.width, outer.x + outer.width) - Math.max(inner.x, outer.x);
        const overlapEnough = hOverlap >= inner.width * 0.8;
        if (insideVertically && overlapEnough) {
          ids.push(b.id);
        }
      }
      map.set(c.id, ids);
    }
    return map;
  }, [blocks, deviceMode]);

  const renderBlock = (rawBlock: BuilderBlock) => {
    // Bilingual: resolve per-locale content + props ONCE at the top of the
    // renderer. We mutate a local `block` copy so every downstream reference
    // (block.content, p.buttonText, p.imageUrl, etc.) auto-picks the active
    // locale's value with legacy-string fallback. Legacy blocks (no *I18n
    // companions) end up identical to before this change.
    // Hebrew is the master truth — read legacy fields directly. Only when
    // rendering English do we look into the I18n companion (with legacy as
    // fallback for blocks without an English translation). This prevents a
    // stale `contentI18n.he` snapshot from masking a subsequently-edited
    // `block.content` value — see BlockRenderer.tsx for the same rule.
    let resolvedContent = rawBlock.content || '';
    if (rendererLocale === 'en') {
      const en = (rawBlock as any).contentI18n?.en;
      if (en != null && en !== '') resolvedContent = en;
    }
    const resolvedProps: any = { ...(rawBlock.props || {}) };
    if (rendererLocale === 'en') {
      for (const key of Object.keys(resolvedProps)) {
        if (!key.endsWith('I18n')) continue;
        const base = key.slice(0, -4);
        const obj = resolvedProps[key];
        if (!obj || typeof obj !== 'object') continue;
        const v = obj.en;
        // Empty strings / arrays = "no translation" → keep the HE legacy.
        const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
        if (!empty) resolvedProps[base] = v;
      }
    }
    const block: BuilderBlock = { ...rawBlock, content: resolvedContent, props: resolvedProps };

    // Live campaign values for {c:…} merge tokens in text/heading blocks.
    // Resolved per the donor's active currency (legacy single-currency
    // fallback); formulas come from this block's props.
    const tokenCtx = {
      raised: ((campaign as any).raisedByCurrency?.[currency] ?? campaign.raised) ?? 0,
      goal: ((campaign as any).goalByCurrency?.[currency] ?? campaign.goal) ?? 0,
      donationCount: (campaign as any).donationCount ?? 0,
      recurringCount: (campaign as any).recurringCount ?? 0,
      currency,
      formulas: (resolvedProps as any)?.textFormulas,
    };
    const subTokens = (c: string) =>
      substituteCampaignTokens(c || '', tokenCtx, rendererLocale === 'en' ? 'en' : 'he');

    // Background blocks always fill the entire canvas — same behavior as
    // the editor. Their stored layout is ignored so adding new content
    // (which grows the canvas) doesn't leave a strip below the background.
    const layout = block.type === 'background'
      ? { x: 0, y: 0, width: canvasWidth, height: canvasHeight }
      : (deviceMode === 'desktop' ? block.desktopLayout : block.mobileLayout);
    if (!layout) return null;

    const s = deviceMode === 'mobile' ? { ...block.style, ...block.mobileStyle } : (block.style || {});
    const p = deviceMode === 'mobile' ? { ...block.props, ...block.mobileProps } : (block.props || {});

    const autoHeight = autoHeightTypes.has(block.type);
    const isShrinkEnabled = SHRINK_ENABLED_TYPES.has(block.type);

    // Dynamic-height blocks (payment-button, ambassadors-public, recent-donors,
    // donation-tiers, heading, text) measure their actual height and push
    // siblings whose authored top is at/below theirs by the measured delta.
    const yOffset = getCumulativeOffset(layout.y, block.id);

    // Tiered z-index — visual blocks (image/gallery/video/container) sit
    // BEHIND content blocks (text/heading/button/form/donation-tiers/etc.)
    // so a placed image never buries the call-to-action. Mirrors the
    // builder's getBlockStackTier so the canvas preview and the live page
    // stack in exactly the same order.
    // 'tabs' is a container tier (1); its member blocks (tabRef) sit above it.
    const mediaTypes = new Set(['image', 'gallery', 'video', 'container', 'tabs']);
    const blockZ = block.type === 'background' ? 0 : (block.tabRef ? 2 : (mediaTypes.has(block.type) ? 1 : 2));

    const wrapStyle: React.CSSProperties = {
      position: 'absolute',
      left: layout.x,
      top: layout.y + yOffset,
      width: layout.width,
      height: autoHeight ? 'auto' : layout.height,
      // Shrink-enabled blocks (ambassadors, recent-donors, text, heading)
      // lose the authored-height floor so they fit their real content — text
      // that wraps to fewer lines on a given device collapses the box and
      // pulls siblings up to the authored gap instead of leaving a dead gap.
      // The remaining autoHeight blocks (donation-tiers, etc.) keep
      // minHeight = authored so their visible boundary stays where the
      // designer placed it. All autoHeight blocks still GROW past authored
      // when content wraps longer (shifts siblings down via getCumulativeOffset).
      minHeight: autoHeight && !isShrinkEnabled ? layout.height : undefined,
      overflow: autoHeight ? 'visible' : 'hidden',
      borderRadius: s.borderRadius ? `${s.borderRadius}px` : undefined,
      // No `top` transition: with dynamic-height measurements firing on
      // initial mount, browser resize, and async data arrival, animating
      // every yOffset change made sibling blocks visibly "jump" around.
      // useLayoutEffect runs the first measurement before the initial paint,
      // so blocks land at their corrected position immediately with no flash.
      zIndex: blockZ,
    };

    const scaledFontSize = s.fontSize || undefined;
    const scaledPadding = s.padding !== undefined ? s.padding : undefined;

    const baseStyle: React.CSSProperties = {
      width: '100%', height: '100%',
      backgroundColor: s.bgTransparent ? 'transparent' : (s.bgColor || undefined),
      backgroundImage: s.bgTransparent ? 'none' : (s.bgGradient || undefined),
      color: s.textColor || undefined,
      fontSize: scaledFontSize ? `${scaledFontSize}px` : undefined,
      fontFamily: s.fontFamily || undefined,
      fontWeight: s.fontWeight || undefined,
      // Bilingual: authored textAlign 'right'/'left' is interpreted as
      // "end-of-reading-direction" / "start-of-reading-direction". Map to
      // logical values so a Hebrew form authored with `right` auto-flips to
      // visual-left in English donors' LTR view. Center stays center.
      textAlign: (() => {
        const a = s.textAlign;
        if (!a) return undefined;
        if (rendererLocale === 'en') {
          if (a === 'right') return 'start';
          if (a === 'left') return 'end';
        }
        return a;
      })() as any,
      padding: scaledPadding !== undefined ? `${scaledPadding}px` : undefined,
      // External margin (ריווח חיצוני) + minHeight — mirror the builder's
      // baseStyle so a block authored with margin/min-height looks identical
      // live. box-sizing keeps the width:100% box from overflowing by padding.
      margin: s.margin !== undefined ? `${s.margin}px` : undefined,
      minHeight: s.minHeight ? `${s.minHeight}px` : undefined,
      boxSizing: 'border-box',
      opacity: s.opacity !== undefined ? s.opacity / 100 : undefined,
      // autoHeight blocks (text, heading, etc.) keep wrapStyle.overflow='visible'
      // so dynamic content can push siblings down. Without borderRadius on this
      // inner background-carrying div, the colored fill paints past the wrapper's
      // rounded corners and the block looks square in the public view.
      borderRadius: s.borderRadius !== undefined ? `${s.borderRadius}px` : undefined,
    };

    switch (block.type) {
      case 'hero': {
        const slideshowImages: string[] = p.slideshowImages || [];
        const hasSlideshow = slideshowImages.length > 1;
        const [slideIdx, setSlideIdx] = React.useState(0);
        React.useEffect(() => {
          if (!hasSlideshow) return;
          const speed = (p.slideshowSpeed || 5) * 1000;
          const timer = setInterval(() => setSlideIdx(prev => (prev + 1) % slideshowImages.length), speed);
          return () => clearInterval(timer);
        }, [hasSlideshow, slideshowImages.length, p.slideshowSpeed]);

        const currentImage = hasSlideshow ? slideshowImages[slideIdx] : p.imageUrl;
        const heroBg = currentImage ? `url(${currentImage})` : (p.bgGradient || s.bgGradient || undefined);
        // imageOpacity (0-100) lets the user use an image as background while
        // letting the colored layer behind it show through.
        const imageLayerOpacity = (typeof p.imageOpacity === 'number') ? Math.max(0, Math.min(100, p.imageOpacity)) / 100 : 1;
        // Block-level opacity applies to bg layers ONLY — not to the text overlay,
        // so title/subtitle stay fully legible.
        const bgOpacity = s.opacity !== undefined ? s.opacity / 100 : 1;

        return (
          <div key={block.id} data-block-id={block.id} style={{ ...wrapStyle, overflow: 'hidden' }}>
            {/* Color layer always rendered behind, so imageOpacity reveals it */}
            {!s.bgTransparent && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: s.bgColor || '#1e40af', opacity: bgOpacity }} />
            )}
            {hasSlideshow ? slideshowImages.map((img: string, i: number) => (
              <div key={i} style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center',
                opacity: (i === slideIdx ? imageLayerOpacity : 0) * bgOpacity,
                transition: 'opacity 1s ease-in-out',
              }} />
            )) : heroBg ? (
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: s.bgTransparent ? 'none' : heroBg,
                backgroundSize: 'cover', backgroundPosition: 'center',
                opacity: imageLayerOpacity * bgOpacity,
              }} />
            ) : null}
            {/* Mirror the builder's hero overlay: text horizontal/vertical
                placement (textPosH/textPosV), alignment, content padding +
                margin and weight, so the live hero matches the editor. */}
            <div style={{
              position: 'relative', zIndex: 1, width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: ({ start: 'flex-start', center: 'center', end: 'flex-end' } as const)[(p.textPosH || 'center') as 'start' | 'center' | 'end'],
              justifyContent: ({ start: 'flex-start', center: 'center', end: 'flex-end' } as const)[(p.textPosV || 'center') as 'start' | 'center' | 'end'],
              textAlign: (s.textAlign || (p.textPosH === 'start' ? 'right' : p.textPosH === 'end' ? 'left' : 'center')) as any,
              color: p.textColor || s.textColor || '#fff',
              padding: p.contentPadding !== undefined ? `${p.contentPadding}px` : (scaledPadding !== undefined ? `${scaledPadding}px` : '32px'),
            }}>
              <div style={{ margin: p.contentMargin !== undefined ? `${p.contentMargin}px` : undefined }}>
                <h1 style={{ fontSize: `${s.fontSize || 36}px`, fontWeight: s.fontWeight ?? 'bold', marginBottom: 8 }}>{block.content || ''}</h1>
                {p.subtitle && <p style={{ fontSize: `${(s.fontSize || 36) * 0.55}px`, opacity: 0.9 }}>{p.subtitle}</p>}
              </div>
            </div>
          </div>
        );
      }
      case 'heading': {
        const isRichH = !!block.props?.isRichText;
        const headingStyle = { fontSize: `${s.fontSize || 28}px`, fontWeight: s.fontWeight || 'bold', color: s.textColor || '#111827' };
        return (
          <div
            key={block.id}
            ref={el => { dynamicBlockRefs.current[block.id] = el; }}
            data-block-id={block.id}
            style={wrapStyle}
          >
            <div style={baseStyle}>
              {isRichH
                ? <div style={headingStyle} dangerouslySetInnerHTML={{ __html: subTokens(sanitizeInlineHtml(block.content || '')) }} />
                : <div style={headingStyle}>{subTokens(block.content || '')}</div>}
            </div>
          </div>
        );
      }
      case 'text': {
        const isRichT = !!block.props?.isRichText;
        const textStyle = { fontSize: `${s.fontSize || 16}px`, color: s.textColor || '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' as const };
        return (
          <div
            key={block.id}
            ref={el => { dynamicBlockRefs.current[block.id] = el; }}
            data-block-id={block.id}
            style={wrapStyle}
          >
            <div style={baseStyle}>
              {isRichT
                ? <p style={textStyle} dangerouslySetInnerHTML={{ __html: subTokens(sanitizeInlineHtml(block.content || '')) }} />
                : <p style={textStyle}>{subTokens(block.content || '')}</p>}
            </div>
          </div>
        );
      }
      case 'image': {
        const imgSlideshow: string[] = p.slideshowImages || [];
        const hasImgSlideshow = imgSlideshow.length > 1;
        const radiusStyle = s.borderRadius ? `${s.borderRadius}px` : undefined;
        const imgPosX = typeof p.posX === 'number' ? p.posX : 50;
        const imgPosY = typeof p.posY === 'number' ? p.posY : 50;
        const imgObjectPosition = `${imgPosX}% ${imgPosY}%`;
        const imgScale = typeof p.posScale === 'number' ? p.posScale : 1;
        // Mirror the editor (BlockRenderer) DOM structure exactly so the
        // PannedImage measures the same wrapper dimensions in both views.
        // Without this, the editor wraps PannedImage in a flex container
        // (alignItems: stretch / center) and the public renders it bare —
        // which can cause subtle differences in how `width/height: 100%`
        // resolves and produces a different crop on the public page.
        const fit = (p.objectFit || 'cover') as string;
        return (
          <div key={block.id} data-block-id={block.id} style={{ ...wrapStyle, opacity: s.opacity !== undefined ? s.opacity / 100 : undefined, overflow: 'hidden' }}>
            <div style={{
              width: '100%', height: '100%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: fit === 'contain' ? 'center' : 'stretch',
              justifyContent: 'center',
            }}>
              {hasImgSlideshow ? (
                <ImageCarousel
                  images={imgSlideshow}
                  mode={p.imageMode === 'carousel' ? 'carousel' : 'slideshow'}
                  fit={fit}
                  objectPosition={imgObjectPosition}
                  radius={radiusStyle}
                  speed={p.slideshowSpeed || 5}
                  autoPlay={p.carouselAutoPlay}
                  arrowColor={p.arrowColor || '#ffffff'}
                  dotColor={p.dotColor || '#ffffff'}
                  alt={p.alt || ''}
                />
              ) : p.url ? (
                <PannedImage
                  url={p.url}
                  alt={p.alt || ''}
                  fit={fit}
                  posX={imgPosX}
                  posY={imgPosY}
                  posScale={imgScale}
                  radius={radiusStyle}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                  תמונה
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'button': {
        // Button fills its wrapper at the authored size (admin sizes by
        // dragging the resize handles) AND grows past it when wrapped text
        // needs more room — without the latter, narrow mobile widths
        // silently clipped two-line labels (the editor canvas was
        // overflow:visible so the full text always showed there, but the
        // live page was overflow:hidden, so any authored height sized to
        // one line on desktop disappeared the moment the label wrapped on
        // a 200px-wide mobile button). Wrapper uses flex column + min-height
        // so the inner button stretches to fill the resolved height
        // (= max(authored, content)). The block is also dynamic-height —
        // when it grows, blocks below shift down (DYNAMIC_HEIGHT_TYPES).
        const btnStyle: React.CSSProperties = {
          backgroundColor: s.bgColor || primary, color: s.textColor || '#fff',
          borderRadius: s.borderRadius !== undefined ? `${s.borderRadius}px` : '8px',
          fontSize: `${s.fontSize || 16}px`, fontWeight: 'bold',
          padding: p.size === 'lg' ? '14px 32px' : p.size === 'sm' ? '6px 16px' : '10px 24px',
          textDecoration: 'none',
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex', alignItems: 'center', justifyContent: p.align || 'center',
          boxSizing: 'border-box', textAlign: 'center',
        };
        const buttonWrap: React.CSSProperties = {
          ...wrapStyle,
          height: 'auto',
          minHeight: layout.height,
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        };
        const isScroll = p.action === 'scrollTo';
        // Compute scroll target ourselves rather than relying on
        // Element.scrollIntoView(). The canvas wrapper uses `zoom: ratio`
        // (non-standard CSS), and several browsers compute scrollIntoView
        // from the element's unscaled offsetTop — landing well before the
        // visually rendered position (often at the top of the page).
        // getBoundingClientRect IS zoom-aware, so adding window.scrollY to
        // the rect's top gives the correct absolute Y in document space.
        const handleScroll = (e: React.MouseEvent) => {
          e.preventDefault();
          if (!p.scrollTargetId) return;
          const doScroll = () => {
            const target = document.querySelector(`[data-block-id="${p.scrollTargetId}"]`) as HTMLElement | null;
            if (!target) return;
            const rect = target.getBoundingClientRect();
            const docTop = rect.top + window.scrollY;
            const pos = p.scrollPosition === 'start' || p.scrollPosition === 'end' ? p.scrollPosition : 'center';
            let top = docTop;
            if (pos === 'center') top = docTop - Math.max(0, (window.innerHeight - rect.height) / 2);
            else if (pos === 'end') top = docTop - Math.max(0, window.innerHeight - rect.height);
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          };
          // If the target is a tabs block and a tab was chosen, open that tab
          // first, then scroll after the switch reflows (so the measured
          // position accounts for the now-visible tab content).
          const targetBlock = (blocks || []).find(b => b.id === p.scrollTargetId);
          if (targetBlock?.type === 'tabs' && p.scrollTargetTab) {
            setActiveTabs(m => ({ ...m, [p.scrollTargetId]: p.scrollTargetTab }));
            requestAnimationFrame(() => requestAnimationFrame(doScroll));
          } else {
            doScroll();
          }
        };
        return (
          <div
            key={block.id}
            ref={el => { dynamicBlockRefs.current[block.id] = el; }}
            data-block-id={block.id}
            style={buttonWrap}
          >
            {isScroll ? (
              <button type="button" onClick={handleScroll} style={{ ...btnStyle, border: 'none', cursor: 'pointer' }}>
                {block.content || t('public.button.defaultLabel')}
              </button>
            ) : (
              <a href={p.href || '#'} style={btnStyle}>
                {block.content || t('public.button.defaultLabel')}
              </a>
            )}
          </div>
        );
      }
      case 'countdown': {
        const [remaining, setRemaining] = React.useState({ days: 0, hours: 0, mins: 0, secs: 0 });
        React.useEffect(() => {
          const update = () => {
            const now = new Date();
            const target = p.targetDate ? new Date(p.targetDate) : new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
            const diff = Math.max(0, target.getTime() - now.getTime());
            setRemaining({
              days: Math.floor(diff / 86400000),
              hours: Math.floor((diff % 86400000) / 3600000),
              mins: Math.floor((diff % 3600000) / 60000),
              secs: Math.floor((diff % 60000) / 1000),
            });
          };
          update();
          const interval = setInterval(update, 1000);
          return () => clearInterval(interval);
        }, [p.targetDate]);

        const unitsBase = [
          { val: String(remaining.days).padStart(2, '0'), label: t('public.countdown.days') },
          { val: String(remaining.hours).padStart(2, '0'), label: t('public.countdown.hours') },
          { val: String(remaining.mins).padStart(2, '0'), label: t('public.countdown.minutes') },
          { val: String(remaining.secs).padStart(2, '0'), label: t('public.countdown.seconds') },
        ];
        const units = p.reverseOrder ? [...unitsBase].reverse() : unitsBase;
        const boxW = p.boxWidth ?? 56;
        const boxH = p.boxHeight ?? 52;
        const boxGap = p.boxGap ?? 12;
        const numSize = p.numFontSize ?? 22;
        const boxBg = s.bgTransparent ? 'transparent' : (s.bgColor || '#2563eb');
        const boxColor = s.textColor || (s.bgTransparent ? (s.bgColor || '#2563eb') : '#fff');
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, backgroundColor: 'transparent', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: boxGap }}>
                {units.map(({ val, label }, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      background: boxBg, color: boxColor,
                      borderRadius: s.borderRadius || 10, width: boxW, height: boxH,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold', fontSize: numSize, fontFamily: s.fontFamily || undefined,
                      border: p.borderWidth ? `${p.borderWidth}px solid ${p.borderColor || '#e5e7eb'}` : (s.bgTransparent ? `2px solid ${s.bgColor || '#2563eb'}` : undefined),
                    }}>{val}</div>
                    {p.showLabels !== false && <span style={{ fontSize: p.labelSize || 11, color: p.labelColor || '#6b7280', fontFamily: p.labelFont || s.fontFamily || undefined, marginTop: 4 }}>{label}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
      case 'video': {
        const embed = parseVideoUrl(p.url, p.startSeconds);
        // Mirror the builder (BlockRenderer): the video plays inside a 16:9
        // (56.25%) aspect box anchored to the TOP of the block — it is NOT
        // stretched to fill the authored box height. Filling the box made a
        // video whose authored box is taller than 16:9 cover whatever the
        // designer placed in the visually-empty area below it in the canvas
        // (e.g. the social icons), since the builder only ever drew the video
        // at its 16:9 height there.
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            {embed ? (
              <div style={{ position: 'relative', paddingBottom: '56.25%', borderRadius: s.borderRadius || 8, overflow: 'hidden' }}>
                {embed.kind === 'iframe' ? (
                  <iframe src={embed.src} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allowFullScreen title="video" />
                ) : (
                  <video src={embed.src} controls style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
            ) : (
              <div style={{ ...baseStyle, background: '#f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', padding: 12, textAlign: 'center', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>🎬</div>
                {!p.url ? t('public.video.noUrl') : <>{t('public.video.invalidUrl')}:<br /><span style={{ fontSize: 11, wordBreak: 'break-all' }}>{p.url}</span></>}
              </div>
            )}
          </div>
        );
      }
      case 'social': {
        const socialIcons: Record<string, React.ReactNode> = {
          whatsapp: <svg viewBox="0 0 24 24" fill="currentColor" width="55%" height="55%"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
          facebook: <svg viewBox="0 0 24 24" fill="currentColor" width="55%" height="55%"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
          instagram: <svg viewBox="0 0 24 24" fill="currentColor" width="55%" height="55%"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
          youtube: <svg viewBox="0 0 24 24" fill="currentColor" width="55%" height="55%"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
          tiktok: <svg viewBox="0 0 24 24" fill="currentColor" width="55%" height="55%"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
          website: <svg viewBox="0 0 24 24" fill="currentColor" width="55%" height="55%"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>,
        };
        const socialNetworks = [
          { key: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
          { key: 'youtube', label: 'YouTube', color: '#FF0000' },
          { key: 'facebook', label: 'Facebook', color: '#1877F2' },
          { key: 'instagram', label: 'Instagram', color: '#E1306C' },
          { key: 'tiktok', label: 'TikTok', color: '#000000' },
          { key: 'website', label: t('public.social.websiteLabel'), color: '#64748b' },
        ];
        const iconSize = p.iconSize || 40;
        const gap = p.gap || 16;
        const canonicalKeys = socialNetworks.map(n => n.key);
        const savedOrder: string[] = Array.isArray(p.socialOrder) ? p.socialOrder.filter((k: string) => canonicalKeys.includes(k)) : [];
        const finalOrder = [...savedOrder, ...canonicalKeys.filter(k => !savedOrder.includes(k))];
        const displayNetworks = finalOrder
          .map(k => socialNetworks.find(n => n.key === k)!)
          .filter(n => n && p[n.key]?.visible !== false);

        // Share URL for this campaign
        const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
        const shareText = campaign.name || t('public.social.campaignFallback');

        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              {/* Social network links */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${gap}px`, justifyContent: p.align || 'center' }}>
                {displayNetworks.map(net => {
                  const url = p[net.key]?.url;
                  const Wrapper = url ? 'a' : 'span';
                  const linkProps = url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {};
                  return (
                    <Wrapper key={net.key} {...linkProps as any} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                      <span style={{ width: iconSize, height: iconSize, borderRadius: '50%', background: net.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'transform 0.2s', cursor: url ? 'pointer' : 'default' }}>
                        {socialIcons[net.key] || net.label.charAt(0)}
                      </span>
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          </div>
        );
      }
      case 'gallery': {
        // Mirror the builder's gallery (LandingPageBuilder/components/BlockRenderer.tsx)
        // 1-to-1: same layout modes (grid/row/column), gap, imageFit, per-image
        // wrapper + border-radius source. Keep these in sync.
        const layoutMode = p.layout || 'grid';
        const cols = p.columns || 3;
        const gap = p.gap || 8;
        const fit = p.imageFit || 'cover';
        const images: string[] = p.images || ['', '', ''];
        const colCount = layoutMode === 'row' ? (images.length || 1) : layoutMode === 'column' ? 1 : cols;
        const radius = s.borderRadius ? `${s.borderRadius}px` : '6px';
        return (
          <div key={block.id} data-block-id={block.id} style={{ ...wrapStyle, overflow: 'hidden' }}>
            <div style={{ ...baseStyle, display: 'grid', gridTemplateColumns: `repeat(${colCount}, 1fr)`, gap, width: '100%', height: '100%' }}>
              {images.map((url: string, i: number) => (
                <div key={i} style={{ width: '100%', height: '100%', borderRadius: radius, overflow: 'hidden', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                  {url ? (
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: fit as any, display: 'block' }} />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      }
      case 'form': {
        // Bilingual: each form field may have label/placeholder/helperText
        // companions (labelI18n, placeholderI18n, helperTextI18n). Resolve
        // each to the active locale BEFORE rendering, so all downstream
        // reads (field.label, field.placeholder, etc.) see the right text.
        // Fields without an EN translation fall back to the legacy HE
        // strings, so existing campaigns are unaffected.
        const rawFormFields = p.formFields || [];
        const formFields = rendererLocale === 'he' ? rawFormFields : rawFormFields.map((f: any) => {
          if (!f) return f;
          const out = { ...f };
          for (const baseKey of ['label', 'placeholder', 'helperText']) {
            const i18nKey = `${baseKey}I18n`;
            const i18nObj = f[i18nKey];
            if (i18nObj && typeof i18nObj === 'object') {
              const v = (i18nObj as any).en;
              if (v != null && v !== '') out[baseKey] = v;
            }
          }
          // Options for select/checkbox-group/radio — each option may have
          // its own I18n variant. Stored as { he: ['a','b'], en: ['A','B'] }
          // under `optionsI18n`. Length match is the user's responsibility.
          if (Array.isArray(f.optionsI18n?.en) && f.optionsI18n.en.length > 0) {
            out.options = f.optionsI18n.en;
          }
          return out;
        });
        // Map known field types to shared donor state. Each donor-state field
        // matches its Hebrew authored label OR English equivalent OR the
        // generic `type` (handled below). Both labels stay in the map so
        // legacy HE-only campaigns + new bilingual campaigns both work.
        const fieldStateMap: Record<string, { value: string; setter: (v: string) => void }> = {
          'שם מלא': { value: donorName, setter: setDonorName },
          'שם': { value: donorName, setter: setDonorName },
          'name': { value: donorName, setter: setDonorName },
          'Full Name': { value: donorName, setter: setDonorName },
          'שם פרטי': { value: donorFirstName, setter: setDonorFirstName },
          'firstName': { value: donorFirstName, setter: setDonorFirstName },
          'first name': { value: donorFirstName, setter: setDonorFirstName },
          'First Name': { value: donorFirstName, setter: setDonorFirstName },
          'שם משפחה': { value: donorLastName, setter: setDonorLastName },
          'lastName': { value: donorLastName, setter: setDonorLastName },
          'last name': { value: donorLastName, setter: setDonorLastName },
          'Last Name': { value: donorLastName, setter: setDonorLastName },
          'אימייל': { value: donorEmail, setter: setDonorEmail },
          'email': { value: donorEmail, setter: setDonorEmail },
          'מייל': { value: donorEmail, setter: setDonorEmail },
          'Email': { value: donorEmail, setter: setDonorEmail },
          'טלפון': { value: donorPhone, setter: setDonorPhone },
          'phone': { value: donorPhone, setter: setDonorPhone },
          'נייד': { value: donorPhone, setter: setDonorPhone },
          'Phone': { value: donorPhone, setter: setDonorPhone },
          'Mobile': { value: donorPhone, setter: setDonorPhone },
        };
        const getFieldState = (field: any) => {
          const label = (field.label || '').trim();
          if (fieldStateMap[label]) return fieldStateMap[label];
          // Bilingual: when rendering at EN we already swapped field.label to
          // the English variant via the labelI18n companion, so the map lookup
          // above hits the English keys ('Email', 'Phone', etc.). For safety,
          // also try the original Hebrew label so a campaign that stored
          // labelI18n.he separate from field.label still maps to donor state.
          const heLabel = (field.labelI18n?.he || '').trim();
          if (heLabel && fieldStateMap[heLabel]) return fieldStateMap[heLabel];
          // Also match by type
          if (field.type === 'email') return fieldStateMap['אימייל'];
          if (field.type === 'tel' || field.type === 'phone') return fieldStateMap['טלפון'];
          // Donor-name fields an org relabeled away from the generic "שם"
          // (e.g. "שם ההורה", "שם התורם", "שם האב/האם"). Without this they
          // fall through to a custom field, leaving donorFirstName empty —
          // which made the donation/receipt name collapse to the "שם משפחה"
          // value sent twice ("מיטב מיטב"). Map to donorFirstName so it pairs
          // with a separate "שם משפחה" as "First Last". Deliberately excludes
          // child/other "שם ..." fields (שם הילד/ה, שם לתפילה), which stay custom.
          const nameNorm = ((field.label || '').trim() || (field.labelI18n?.he || '').trim()).replace(/\s+/g, ' ');
          if ((field.type === 'text' || !field.type) &&
              /^שם\s+(ההורה|הורה|ההורים|התורם|המשלם|המבקש|האב|האם)$/.test(nameNorm)) {
            return { value: donorFirstName, setter: setDonorFirstName };
          }
          // Custom field — key by field.id (unique per field). Keying by
          // label collapsed two fields with the same label into one shared
          // value, so typing in one mirrored into the others.
          const key = field.id;
          return {
            value: customFieldValues[key] || '',
            setter: (v: string) => setCustomFieldValues(prev => ({ ...prev, [key]: v })),
          };
        };

        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            {/* The builder renders the form as a white card with shadow-sm
                (BlockRenderer `className="bg-white shadow-sm"`). Mirror that
                here so the public form isn't transparent when the designer
                left the default background — bgColor / bgTransparent still
                win when explicitly set. */}
            <div style={{
              ...baseStyle,
              backgroundColor: baseStyle.backgroundColor || '#fff',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            }}>
              {/* dir follows the donor's locale so RTL-only layouts read
                  naturally for English donors. Hebrew donors keep RTL (the
                  legacy hard-coded behavior). */}
              <form style={{ display: 'flex', flexWrap: 'wrap', gap: p.fieldGap ?? 12 }} dir={rendererLocale === 'en' ? 'ltr' : 'rtl'} onSubmit={e => e.preventDefault()}>
                {formFields.map((field: any) => {
                  const state = getFieldState(field);
                  // textarea / select / checkbox / radio / checkbox-group don't
                  // map onto the shared donor inputs — they always go through
                  // customFieldValues. Key by field.id (unique) rather than
                  // label so duplicate labels don't collide into one slot.
                  const customKey = field.id;
                  const customVal = customFieldValues[customKey];
                  const setCustom = (v: any) => setCustomFieldValues(prev => ({ ...prev, [customKey]: v }));

                  let control: React.ReactNode;
                  if (field.type === 'textarea') {
                    control = (
                      <textarea
                        placeholder={field.placeholder || ''}
                        rows={field.rows || 3}
                        value={customVal ?? ''}
                        onChange={e => setCustom(e.target.value)}
                        style={fsTextarea(p, field)}
                      />
                    );
                  } else if (field.type === 'select') {
                    control = (
                      <select value={customVal ?? ''} onChange={e => setCustom(e.target.value)} style={fsInput(p, field)}>
                        <option value="">{field.placeholder || t('public.form.selectPlaceholder')}</option>
                        {(field.options || []).map((opt: string, oi: number) => <option key={oi} value={opt}>{opt}</option>)}
                      </select>
                    );
                  } else if (field.type === 'checkbox') {
                    control = (
                      <CheckboxOption
                        checked={!!customVal}
                        onToggle={() => setCustom(!customVal)}
                        label={field.placeholder || field.label}
                        variant={field.checkboxVariant || p.checkboxVariant || 'square'}
                        color={field.checkboxColor || p.checkboxColor || '#2563eb'}
                        size={field.checkboxSize || p.checkboxSize || 18}
                        textColor={p.checkboxLabelColor || '#374151'}
                        textSize={field.fontSize || p.inputFontSize || 14}
                      />
                    );
                  } else if (field.type === 'radio') {
                    control = (
                      <div style={{
                        display: 'flex',
                        flexDirection: (field.optionsLayout || 'column') === 'row' ? 'row' : 'column',
                        flexWrap: 'wrap',
                        gap: field.optionsLayout === 'row' ? 8 : 6,
                      }}>
                        {(field.options || []).map((opt: string, oi: number) => (
                          <CheckboxOption
                            key={oi}
                            isRadio
                            checked={customVal === opt}
                            onToggle={() => setCustom(opt)}
                            label={opt}
                            variant={field.checkboxVariant || p.checkboxVariant || 'square'}
                            color={field.checkboxColor || p.checkboxColor || '#2563eb'}
                            size={field.checkboxSize || p.checkboxSize || 18}
                            textColor={p.checkboxLabelColor || '#374151'}
                            textSize={field.fontSize || p.inputFontSize || 14}
                          />
                        ))}
                      </div>
                    );
                  } else if (field.type === 'checkbox-group') {
                    const arr: string[] = Array.isArray(customVal) ? customVal : [];
                    control = (
                      <div style={{
                        display: 'flex',
                        flexDirection: (field.optionsLayout || 'column') === 'row' ? 'row' : 'column',
                        flexWrap: 'wrap',
                        gap: field.optionsLayout === 'row' ? 8 : 6,
                      }}>
                        {(field.options || []).map((opt: string, oi: number) => {
                          const isChecked = arr.includes(opt);
                          return (
                            <CheckboxOption
                              key={oi}
                              checked={isChecked}
                              onToggle={() => setCustom(isChecked ? arr.filter(x => x !== opt) : [...arr, opt])}
                              label={opt}
                              variant={field.checkboxVariant || p.checkboxVariant || 'square'}
                              color={field.checkboxColor || p.checkboxColor || '#2563eb'}
                              size={field.checkboxSize || p.checkboxSize || 18}
                              textColor={p.checkboxLabelColor || '#374151'}
                              textSize={field.fontSize || p.inputFontSize || 14}
                            />
                          );
                        })}
                      </div>
                    );
                  } else {
                    const inputType = field.type === 'tel' ? 'tel'
                      : field.type === 'email' ? 'email'
                      : field.type === 'number' ? 'number'
                      : field.type === 'date' ? 'date'
                      : 'text';
                    control = (
                      <input
                        type={inputType}
                        placeholder={field.placeholder || ''}
                        value={state?.value ?? ''}
                        onChange={e => state?.setter(e.target.value)}
                        style={fsInput(p, field)}
                      />
                    );
                  }

                  return (
                    <div key={field.id} style={fsFieldWrap(field)}>
                      {!field.hideLabel && field.type !== 'checkbox' && (
                        <label style={fsLabel(p, field)}>
                          {field.label}{field.required && <span style={fsRequired(p)}>*</span>}
                        </label>
                      )}
                      {control}
                    </div>
                  );
                })}
              </form>
            </div>
          </div>
        );
      }
      case 'donation-tiers': {
        const rawTiers: any[] = p.tiers || campaign.donationTiers || [];
        // Bilingual: when the donor is on the EN page, prefer each tier's
        // English nameI18n / descriptionI18n. Falls back to the legacy
        // single-string fields so non-translated tiers still render.
        const tiers = rendererLocale === 'en' ? rawTiers.map((tier: any) => {
          if (!tier) return tier;
          const out: any = { ...tier };
          const nameEn = tier.nameI18n?.en;
          if (nameEn != null && nameEn !== '') out.name = nameEn;
          const descEn = tier.descriptionI18n?.en;
          if (descEn != null && descEn !== '') out.description = descEn;
          return out;
        }) : rawTiers;

        // 'row' is honored as-authored on mobile too — when the designer picks
        // a single row we keep all tiers side-by-side (columns just get
        // narrower) instead of silently re-wrapping to 2 columns.
        const tierLayout = p.layout || 'row';
        const tierGridCols = tierLayout === '2cols' ? 'repeat(2, 1fr)'
          : tierLayout === '2rows' ? `repeat(${Math.ceil(tiers.length / 2)}, 1fr)`
          : `repeat(${Math.min(tiers.length, 4)}, 1fr)`;
        const tierGridStyle = tierLayout === '2rows'
          ? { display: 'grid' as const, gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }
          : { display: 'grid' as const, gridTemplateColumns: tierGridCols, gap: 12 };
        // Per-viewport TEXT size multiplier for the tier buttons (set in the
        // builder's DesignPanel). Desktop and mobile are independent; default 1
        // (=100%). Scales ONLY the fonts — padding stays fixed.
        const tierFont = deviceMode === 'mobile'
          ? (typeof p.tierSizeMobile === 'number' ? p.tierSizeMobile : 1)
          : (typeof p.tierSizeDesktop === 'number' ? p.tierSizeDesktop : 1);

        return (
          <div
            key={block.id}
            ref={el => { dynamicBlockRefs.current[block.id] = el; }}
            data-block-id={block.id}
            style={wrapStyle}
          >
            <div style={{ ...baseStyle, ...tierGridStyle }}>
              {tiers.map((tier: any, i: number) => {
                const tierKey = `${block.id}:${tier._id ?? i}`;
                const sel = selectedTierKey === tierKey && !customAmount;
                // Multi-currency: tier.amountByCurrency holds the per-currency
                // override (e.g. { ILS: 100, USD: 36 }). Falls back to the
                // legacy `tier.amount` when the active currency wasn't set in
                // the builder — so single-currency campaigns are unchanged.
                const tierAmountForCurrency: number = (
                  tier.amountByCurrency && typeof tier.amountByCurrency[currency] === 'number'
                    ? tier.amountByCurrency[currency]
                    : tier.amount
                );
                return (
                  <div
                    key={tier._id || i}
                    onClick={() => {
                      setSelectedTier(tierAmountForCurrency);
                      setSelectedTierKey(tierKey);
                      setCustomAmount('');
                      // Tier flagged as recurring → auto-tick the recurring
                      // checkbox and seed the months count from the tier so
                      // the user doesn't have to redo it manually.
                      if ((tier as any).isRecurring) {
                        setIsRecurring(true);
                        if (typeof (tier as any).recurringMonths === 'number') {
                          setRecurringMonths(Math.max(3, (tier as any).recurringMonths));
                        }
                      }
                    }}
                    style={{
                      // Keep the card fully opaque on selection — feedback
                      // comes from a thicker border and an outer ring rather
                      // than from a near-transparent tint that hid the text.
                      background: s.bgColor || '#ffffff',
                      borderRadius: s.borderRadius || 12,
                      boxShadow: sel
                        ? `0 0 0 4px ${primary}33, 0 4px 12px rgba(0,0,0,0.12)`
                        : '0 2px 8px rgba(0,0,0,0.10)',
                      padding: 20,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      border: `${sel ? 3 : 2}px solid ${sel ? primary : '#E5E7EB'}`,
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ fontSize: 28 * tierFont, fontWeight: 'bold', color: s.textColor || '#111827' }}>
                      {currencySymbol(currency)}{Number(tierAmountForCurrency || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 16 * tierFont, fontWeight: 600, color: s.textColor || '#374151' }}>
                      {tier.name}
                    </div>
                    {tier.description && (
                      <div style={{ fontSize: 13 * tierFont, color: '#6b7280' }}>
                        {tier.description}
                      </div>
                    )}
                    {(tier as any).isRecurring && (
                      <div style={{
                        fontSize: 9 * tierFont, fontWeight: 500, color: '#6b7280',
                      }}>
                        {t('public.donationTiers.recurringFor', { n: (tier as any).recurringMonths || 12 })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      case 'ambassadors-public': {
        // Live ambassador roster from /public/campaigns/:slug/ambassadors-public.
        // Falls back to the campaign's static ambassador list if the fetch
        // failed (so the block still renders names even with no donations).
        const fallback = (campaign.ambassadors || []).map((a: any) => ({
          name: a.name, slug: a.slug, goal: a.goal || 0, raised: a.raisedAmount || 0,
          donorsCount: 0, recentDonors: [] as Array<{ name: string; amount: number; createdAt: string }>,
        }));
        const list: AmbassadorItem[] = ambassadorsPublic.length > 0 ? ambassadorsPublic : fallback;
        // Clicking an ambassador card attributes the donor's incoming
        // donation to that ambassador (writes ?ref=slug into the URL).
        // replaceState + popstate keeps the rest of the URL intact and lets
        // useSearchParams() in this page re-flow without a full reload, so
        // the donation form picks up the new ref.
        const onAmbassadorClick = (slug: string) => {
          const next = new URLSearchParams(window.location.search);
          next.set('ref', slug);
          window.history.replaceState({}, '', `${window.location.pathname}?${next.toString()}${window.location.hash}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
          // Take the donor straight to the donation form so the just-selected
          // ambassador is clearly the one they're about to give through.
          const formEl = paymentBlock ? dynamicBlockRefs.current[paymentBlock.id] : null;
          formEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };

        return (
          <div
            key={block.id}
            ref={el => { dynamicBlockRefs.current[block.id] = el; }}
            data-block-id={block.id}
            style={wrapStyle}
          >
            <div style={baseStyle}>
              <AmbassadorsPublicBlock
                list={list}
                s={s}
                p={p}
                primary={primary}
                isMobile={deviceMode === 'mobile'}
                onCardClick={onAmbassadorClick}
                activeSlug={ambassadorRef}
                campaignSlug={campaign.slug}
              />
            </div>
          </div>
        );
      }
      case 'recent-donors': {
        // Always render newest-first so the order matches the "X minutes ago"
        // timestamps shown on each row. Sort defensively on the client rather
        // than trusting the source endpoint's ordering.
        // When any live feature is on (popup toasts, live meter, or confetti)
        // the poll keeps a fresh donor list in liveRecentDonors — use it so the
        // block updates without a reload; otherwise fall back to the load-time
        // snapshot.
        const donorsSource = (livePollEnabled && liveRecentDonors)
          ? liveRecentDonors
          : (campaign.recentDonors || []);
        const donors: any[] = [...donorsSource].sort((a, b) => {
          const ta = new Date(a.date || a.createdAt || 0).getTime();
          const tb = new Date(b.date || b.createdAt || 0).getTime();
          return tb - ta;
        });
        const minDonors = p.minDonors || 0;
        // Recent-donors list is capped at 5 (block option max); clamp here too
        // so campaigns saved before the cap don't keep showing more.
        const maxDonors = Math.min(5, p.maxDonors || 5);
        const title = p.title || t('public.recentDonors.title');

        // Resolve the optional extra form-field selected in the block editor.
        // The donor's name/email/phone live as top-level fields on the donation
        // (donorName/donorEmail/donorPhone) while everything else is in
        // customFields. We look up the field definition across all form blocks
        // to get its label and to decide which donor field to read from.
        const extraFieldId: string | undefined = p.extraFieldId || undefined;
        const showExtraLabel = p.extraFieldShowLabel !== false;
        let extraFieldDef: { id: string; label: string; type?: string } | null = null;
        if (extraFieldId) {
          for (const b of (campaign.blocks || []) as any[]) {
            if (b?.type !== 'form') continue;
            const fields = (b.props?.formFields || []) as Array<{ id: string; label?: string; type?: string }>;
            const match = fields.find(f => f?.id === extraFieldId);
            if (match) {
              extraFieldDef = { id: match.id, label: match.label || match.id, type: match.type };
              break;
            }
          }
        }
        const readExtraValue = (donor: any): string => {
          if (!extraFieldDef) return '';
          const label = (extraFieldDef.label || '').trim().toLowerCase();
          const type = extraFieldDef.type;
          // Email/phone/name are not in customFields — they map to dedicated
          // donor.* fields on the server. Mirror that lookup here.
          if (type === 'email' || label === 'email' || label === 'אימייל' || label === 'מייל') return donor.email || '';
          if (type === 'tel' || type === 'phone' || label === 'טלפון' || label === 'נייד' || label === 'phone') return donor.phone || '';
          if (label === 'שם' || label === 'שם מלא' || label === 'name') return donor.name || '';
          const cf = donor.customFields || {};
          const v = cf[extraFieldDef.id];
          return v == null ? '' : String(v);
        };

        const donorViews = donors.slice(0, maxDonors).map((donor: any) => {
          const fullName = (donor.name || donor.clientName || '').trim();
          const extraValue = readExtraValue(donor);
          // Opt-in (block prop showAmbassador): "via <ambassador>" when the
          // donation was attributed to one. Bilingual name resolves through
          // the same resolver the rest of the page uses.
          const ambassadorName = p.showAmbassador === true
            ? (resolveStr(donor.ambassadorNameI18n, rendererLocale) || donor.ambassadorName || '')
            : '';
          return {
            name: fullName,
            amount: donor.amount || 0,
            ambassadorName,
            extraLabel: extraFieldDef && showExtraLabel ? extraFieldDef.label : undefined,
            extraValue: extraFieldDef ? extraValue : undefined,
            timeLabel: (donor.date || donor.createdAt) ? formatTimeAgo(donor.date || donor.createdAt) : undefined,
          };
        });
        return (
          <div
            key={block.id}
            ref={el => { dynamicBlockRefs.current[block.id] = el; }}
            data-block-id={block.id}
            style={wrapStyle}
          >
            <div style={{ ...baseStyle, overflow: 'visible' }}>
              <RecentDonorsBlock
                donors={donorViews}
                title={title}
                primary={primary}
                locale={rendererLocale}
                showAmount={p.showAmount !== false}
                showAmbassador={p.showAmbassador === true}
                hasExtraField={!!extraFieldDef}
                displayStyle={p.displayStyle || 'list'}
                titleColor={s.textColor}
                typo={donorTypoFromProps(p)}
              />
            </div>
          </div>
        );
      }
      case 'progress': {
        // Route everything through DynamicProgress so all display modes (bar/circle/
        // thermometer/number) behave consistently. Campaign raised/goal flow in via
        // campaignData — DynamicProgress handles both 'campaign' and legacy 'static'
        // (falling back to manual current/target if the user overrides).
        const effectiveSource = p.dataSource || 'campaign';
        const effProps = effectiveSource === 'static' ? p : { ...p, dataSource: effectiveSource };
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <DynamicProgress
              props={effProps}
              style={s}
              baseStyle={baseStyle}
              campaignData={{
                // When liveMeter is on these come from the poll-updated
                // liveStats so the amount counts up to each new total; otherwise
                // they're the values loaded with the page (byte-identical).
                raised: (liveMeter ? liveStats.raised : campaign.raised) ?? 0,
                goal: campaign.goal ?? 0,
                recurringCount: (liveMeter ? liveStats.recurringCount : (campaign as any).recurringCount) ?? 0,
                donationCount: (liveMeter ? liveStats.donationCount : (campaign as any).donationCount) ?? 0,
                // Multi-currency: pass the maps so the block can pick a
                // per-currency raised/goal pair based on activeCurrency.
                raisedByCurrency: liveMeter ? liveStats.raisedByCurrency : (campaign as any).raisedByCurrency,
                goalByCurrency: (campaign as any).goalByCurrency,
              }}
              activeCurrency={currency}
              editLocale={rendererLocale}
              animateValue={liveMeter}
              playEntrance
            />
          </div>
        );
      }
      case 'chart': {
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <DynamicChart props={p} style={s} baseStyle={baseStyle} />
          </div>
        );
      }
      case 'payment-button': {
        const currentAmount = getAmount();
        // Animation & hover apply only to the button, not the whole component
        const btnAnim = s.animationType && s.animationType !== 'none'
          ? { animation: `anim-${s.animationType} ${s.animationDuration || 600}ms ease-out ${s.animationDelay || 0}ms both` }
          : {};
        const [btnHover, setBtnHover] = React.useState<React.CSSProperties>({});
        const btnHasHover = s.hoverEffect && s.hoverEffect !== 'none';

        return (
          <div key={block.id} ref={paymentBlockRef} data-block-id={block.id} style={{ ...wrapStyle, background: 'none', boxShadow: 'none', zIndex: 5 }}>
            <div style={{ ...baseStyle, backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, justifyContent: 'center', maxWidth: 400, margin: s.margin !== undefined ? `${s.margin}px auto` : '0 auto' }}>
              {p.showAmountInput && (() => {
                // Multi-currency: prefer the per-currency default when the
                // builder has set one (p.defaultAmountByCurrency['USD']=36),
                // else fall back to the legacy single ILS-only p.defaultAmount.
                const byCcy = (p.defaultAmountByCurrency || {}) as Record<string, number>;
                const placeholderAmount: number = (
                  typeof byCcy[currency] === 'number'
                    ? byCcy[currency]
                    : (p.defaultAmount || 100)
                );
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 700 }}>{currencySymbol(currency)}</span>
                    <input
                      type="number"
                      value={customAmount || (selectedTier ? String(selectedTier) : '')}
                      onChange={e => { setCustomAmount(e.target.value); setSelectedTier(null); setSelectedTierKey(null); }}
                      placeholder={String(placeholderAmount)}
                      style={{ width: 120, padding: '10px 14px', borderRadius: 10, border: '2px solid #e5e7eb', fontSize: 18, textAlign: 'center', fontWeight: 700 }}
                    />
                  </div>
                );
              })()}
              {/* Currency selection lives in the floating top-end widget now —
                  this inline toggle was removed to declutter the donation
                  block. Donors flip currency once at the page level. */}
              {p.showRecurringToggle && campaign.recurringEnabled !== false && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: '100%' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: p.recurringLabelColor || '#6b7280', cursor: 'pointer' }}>
                    <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} style={{ width: 14, height: 14 }} />
                    {t('public.payment.recurringHeader')}
                  </label>
                  {isRecurring && (
                    <select
                      value={recurringMonths}
                      onChange={e => setRecurringMonths(+e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, color: '#374151', cursor: 'pointer' }}
                    >
                      {/* 3–60 months, then an unlimited option. 999 is the
                          system-wide sentinel for "no limit" standing orders
                          (recurringChargeJob treats installmentTotal >= 999 as
                          open-ended). */}
                      {Array.from({ length: 58 }, (_, i) => i + 3).map(m => (
                        <option key={m} value={m}>{t('public.payment.monthsCount', { n: m })}</option>
                      ))}
                      <option value={RECURRING_UNLIMITED}>{t('public.payment.monthsUnlimited')}</option>
                    </select>
                  )}
                  {isRecurring && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151' }}>
                      <span>{t('public.payment.chargeDayLabel')}</span>
                      <select
                        value={chargingDay}
                        onChange={e => setChargingDay(+e.target.value)}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, color: '#374151', cursor: 'pointer' }}
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {isRecurring && (
                    <p style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5, margin: 0, textAlign: 'center', maxWidth: 420 }}>
                      {t('public.payment.chargeDayNote', { day: chargingDay })}
                    </p>
                  )}
                  {isRecurring && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 10, width: '100%', maxWidth: 420 }}>
                      <p style={{ fontSize: 11, color: '#78350f', lineHeight: 1.5, margin: 0, marginBottom: 8 }}>
                        {/* recurringConsent in the locale files holds the full sentence;
                            the variable parts (amount, months) are rendered separately
                            so number formatting stays clean. */}
                        <strong>{t('public.payment.recurringConsent').split(',')[0]}:</strong> {t('public.payment.recurringConsent').split(',').slice(1).join(',')}
                        <br />{currencySymbol(currency)}{getAmount().toLocaleString(getBootLocale() === 'en' ? 'en-US' : 'he-IL')} × {recurringMonths >= RECURRING_UNLIMITED ? t('public.payment.monthsUnlimited') : t('public.payment.monthsCount', { n: recurringMonths })}
                      </p>
                      {/* Transparency for non-ILS recurring: bank-side standing
                          orders in foreign currency route through Tranzila's
                          STO API (not YaadPay soft protocol), and the donor
                          should know the per-month charge is in the same
                          foreign currency. */}
                      {currency !== 'ILS' && (
                        <p style={{ fontSize: 11, color: '#78350f', lineHeight: 1.5, margin: '0 0 8px', fontStyle: 'italic' }}>
                          {currency === 'USD'
                            ? `סליקה חוזרת בדולר תתבצע דרך Tranzila. החיוב יבוצע ב-${currencySymbol(currency)}${getAmount().toLocaleString('en-US')} בכל חודש.`
                            : `סליקה חוזרת ב${currency} תתבצע דרך Tranzila. החיוב יבוצע ב-${currencySymbol(currency)}${getAmount().toLocaleString('en-US')} בכל חודש.`}
                        </p>
                      )}
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={recurringConsent} onChange={e => setRecurringConsent(e.target.checked)} style={{ marginTop: 2, width: 14, height: 14 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#78350f' }}>{t('public.payment.recurringConsent')}</span>
                      </label>
                    </div>
                  )}
                </div>
              )}
              {/* Anonymous donation — hides the donor's name from the public
                  recent-donors / ambassadors lists and live toasts. The name
                  is still submitted (income + receipts show it). */}
              {p.showAnonymousOption !== false && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 6,
                    fontSize: 12,
                    color: p.recurringLabelColor || '#6b7280',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 420,
                    lineHeight: 1.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={donateAnonymously}
                    onChange={e => setDonateAnonymously(e.target.checked)}
                    style={{ marginTop: 2, width: 14, height: 14, flexShrink: 0 }}
                  />
                  <span>{t('public.payment.anonymousOption')}</span>
                </label>
              )}
              {/* Terms of service acceptance — required for credit-card clearing compliance */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  fontSize: 12,
                  color: p.recurringLabelColor || '#6b7280',
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: 420,
                  lineHeight: 1.5,
                }}
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: 2, width: 14, height: 14, flexShrink: 0 }}
                />
                <span>
                  {t('public.payment.termsAccept')}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: p.buttonColor || '#dc2626', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {t('public.payment.termsLink')}
                  </a>
                  {t('public.payment.and')}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: p.buttonColor || '#dc2626', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {t('public.payment.privacyLink')}
                  </a>
                </span>
              </label>
              {donationError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 8, color: '#dc2626', fontSize: 12, textAlign: 'center', width: '100%' }}>
                  {donationError}
                </div>
              )}
              <button
                onClick={(campaign?.showPaymentMethodPicker && !isRecurring && walletPickerEligible) ? openPicker : handleDonate}
                disabled={paymentLoading}
                onMouseEnter={btnHasHover ? () => setBtnHover(getHoverEnterStyle(s.hoverEffect as any, p.buttonColor || primary)) : undefined}
                onMouseLeave={btnHasHover ? () => setBtnHover(getHoverLeaveStyle(s.hoverEffect as any)) : undefined}
                style={{
                  background: p.buttonColor || '#dc2626',
                  color: p.buttonTextColor || '#fff',
                  border: 'none',
                  borderRadius: s.borderRadius !== undefined ? `${s.borderRadius}px` : 14,
                  padding: '14px 40px',
                  fontSize: 18, fontWeight: 800,
                  cursor: paymentLoading ? 'not-allowed' : 'pointer',
                  opacity: paymentLoading ? 0.7 : 1,
                  transition: 'all 0.3s',
                  width: '100%',
                  ...btnAnim,
                  ...btnHover,
                }}
              >
                {/* Bilingual button text — see BlockRenderer payment-button
                    for the rationale. English donors never see the HE master
                    text; designers see they need an EN translation. */}
                {paymentLoading ? t('public.payment.processing') : (() => {
                  const rawProps = rawBlock.props || {};
                  const i18n = rawProps.buttonTextI18n;
                  if (rendererLocale === 'en') {
                    if (i18n?.en) return i18n.en;
                    return t('public.donateNow');
                  }
                  return rawProps.buttonText || t('public.donateNow');
                })()}
              </button>
              {/* Dedicated BIT button — brand colours (dark teal + cyan
                  wordmark). Shown only when the org has BIT configured, the
                  donation isn't recurring and the currency is ILS. Opens the
                  payment modal straight into the BIT flow. */}
              {p.showBitButton && bitButtonAvailable && !isRecurring && currency === 'ILS' && (
                <button
                  onClick={handleBitDonate}
                  disabled={paymentLoading}
                  style={{
                    background: 'linear-gradient(135deg,#16383C,#0F2E31)',
                    color: '#fff', border: 'none',
                    borderRadius: s.borderRadius !== undefined ? `${s.borderRadius}px` : 14,
                    padding: '14px 40px', fontSize: 18, fontWeight: 800,
                    cursor: paymentLoading ? 'not-allowed' : 'pointer',
                    opacity: paymentLoading ? 0.7 : 1,
                    width: '100%', lineHeight: 1.2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <span>תשלום מהיר ב־</span>
                  <span style={{ color: '#57DEDF', fontFamily: "'Varela Round', sans-serif", fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>bit</span>
                </button>
              )}
              {/* External payment alongside the built-in form — secondary
                  button under the main one (mode='replace' reroutes the main
                  button instead, inside handleDonate). */}
              {campaign?.externalPayment?.provider && campaign?.externalPayment?.mode === 'alongside' && (
                <button
                  onClick={() => {
                    if (campaign?.externalPayment?.provider === 'nedarim') {
                      // Nedarim needs the donor details from our form.
                      const amount = getAmount();
                      if (!amount || amount <= 0) { setDonationError(t('public.errors.enterAmount')); return; }
                      if (!getFullName()) { setDonationError(t('public.errors.enterName')); return; }
                      if (!termsAccepted) { setDonationError(t('public.errors.acceptTerms')); return; }
                      setDonationError(null);
                    }
                    openExternalPayment();
                  }}
                  style={{
                    background: 'transparent',
                    color: p.buttonColor || '#dc2626',
                    border: `2px solid ${p.buttonColor || '#dc2626'}`,
                    borderRadius: s.borderRadius !== undefined ? `${s.borderRadius}px` : 14,
                    padding: '10px 40px',
                    fontSize: 15, fontWeight: 700,
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: 8,
                  }}
                >
                  {campaign?.externalPayment?.provider === 'nedarim' ? 'תרומה דרך נדרים פלוס' : 'תרומה דרך jGive'}
                </button>
              )}
              {/* Loading spinner */}
              {paymentLoading && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: `3px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 8px' }} />
                  <p style={{ color: primary, fontWeight: 600, fontSize: 13 }}>{t('public.payment.processing')}</p>
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'external-payment': {
        // External payment surface. A manually-entered URL wins over
        // everything; otherwise provider resolution: explicit block choice →
        // campaign-level setting → whichever provider is actually configured
        // (Nedarim integration / jGive URL).
        const customUrl = String(p.customUrl || '').trim();

        // Modal mode: only a button renders in the page — the payment surface
        // opens in a popup (same UX as the built-in Tranzila/HyP button).
        if (p.displayMode === 'modal') {
          const jgiveUrlM = campaign?.externalPayment?.jgiveUrl || campaign?.jgiveIframeUrl || '';
          const nedarimReadyM = Boolean(campaign?.nedarimIframe?.mosadId);
          let providerM: string = p.provider && p.provider !== 'auto'
            ? p.provider
            : (campaign?.externalPayment?.provider || (nedarimReadyM ? 'nedarim' : (jgiveUrlM ? 'jgive' : '')));
          if (providerM === 'nedarim' && !nedarimReadyM) providerM = jgiveUrlM ? 'jgive' : '';
          if (providerM === 'jgive' && !jgiveUrlM) providerM = nedarimReadyM ? 'nedarim' : '';
          if (!customUrl && !providerM) return null; // nothing configured

          const openModal = () => {
            if (customUrl) {
              setExtBlockModal({ url: customUrl, title: p.title || 'תשלום מאובטח' });
            } else if (providerM === 'nedarim') {
              setExtBlockModal({
                url: buildNedarimOnlineUrl(campaign!.nedarimIframe!.mosadId, {
                  groupe: campaign?.nedarimGroupe || campaign?.name || campaign?.slug || '',
                  currency: campaign?.defaultCurrency || 'ILS',
                  param1: campaign?._id ? `campaign:${campaign?._id}` : '',
                  param2: ambassadorRef ? `ambassador:${ambassadorRef}` : '',
                }),
                title: p.title || 'תרומה דרך נדרים פלוס',
              });
            } else {
              setExtBlockModal({ url: jgiveUrlM, title: p.title || 'תרומה דרך jGive' });
            }
          };
          return (
            <div key={block.id} data-block-id={block.id} style={{ ...wrapStyle, zIndex: 5 }}>
              <div style={{ ...baseStyle, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10 }}>
                {p.title && (
                  <div style={{ fontSize: 17, fontWeight: 700, textAlign: 'center' }}>{p.title}</div>
                )}
                <button
                  onClick={openModal}
                  style={{
                    width: '100%', padding: '14px 24px', border: 'none', borderRadius: 12,
                    background: p.accentColor || primary, color: '#fff', fontSize: 16, fontWeight: 700,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                  }}
                >
                  {p.buttonText || 'לתרומה 💳'}
                </button>
              </div>
            </div>
          );
        }

        if (customUrl) {
          return (
            <div key={block.id} data-block-id={block.id} style={{ ...wrapStyle, zIndex: 5 }}>
              <div style={{ ...baseStyle, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {p.title && (
                  <div style={{ fontSize: 17, fontWeight: 700, textAlign: 'center' }}>{p.title}</div>
                )}
                <iframe
                  src={customUrl}
                  title={p.title || 'תשלום מאובטח'}
                  style={{ width: '100%', height: Math.max(200, layout.height - (p.title ? 60 : 20)), border: 0, borderRadius: 12 }}
                  allow="payment *"
                />
              </div>
            </div>
          );
        }

        const jgiveUrl = campaign?.externalPayment?.jgiveUrl || campaign?.jgiveIframeUrl || '';
        const nedarimReady = Boolean(campaign?.nedarimIframe?.mosadId);
        let provider: string = p.provider && p.provider !== 'auto'
          ? p.provider
          : (campaign?.externalPayment?.provider || (nedarimReady ? 'nedarim' : (jgiveUrl ? 'jgive' : '')));
        if (provider === 'nedarim' && !nedarimReady) provider = jgiveUrl ? 'jgive' : '';
        if (provider === 'jgive' && !jgiveUrl) provider = nedarimReady ? 'nedarim' : '';
        if (!provider) return null; // nothing configured — render nothing

        return (
          <div key={block.id} data-block-id={block.id} style={{ ...wrapStyle, zIndex: 5 }}>
            <div style={{ ...baseStyle, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {p.title && (
                  <div style={{ fontSize: 17, fontWeight: 700, textAlign: 'center' }}>{p.title}</div>
                )}
                <iframe
                  src={provider === 'nedarim'
                    ? buildNedarimOnlineUrl(campaign.nedarimIframe!.mosadId, {
                        groupe: campaign?.nedarimGroupe || campaign?.name || campaign?.slug || '',
                        currency: campaign?.defaultCurrency || 'ILS',
                        param1: campaign?._id ? `campaign:${campaign?._id}` : '',
                        param2: ambassadorRef ? `ambassador:${ambassadorRef}` : '',
                      })
                    : jgiveUrl}
                  title={provider === 'nedarim' ? 'תרומה דרך נדרים פלוס' : 'תרומה דרך jGive'}
                  style={{ width: '100%', height: Math.max(200, layout.height - (p.title ? 60 : 20)), border: 0, borderRadius: 12 }}
                  allow="payment *"
                />
              </div>
            </div>
          </div>
        );
      }
      case 'background': {
        // Background spans its authored pixel height — same as in the editor,
        // since sibling blocks render at their authored coords (no overflow
        // bookkeeping pushing them down anymore).
        const bgWrapStyle: React.CSSProperties = {
          ...wrapStyle,
          height: layout.height,
          zIndex: 0,
        };
        return (
          <div key={block.id} data-block-id={block.id} style={bgWrapStyle}>
            {/* Layered pattern/gradient/colour — shared with the editor.
                In full-bleed wide-screen mode the OUTER wrapper already paints
                the background edge-to-edge (and unzoomed), so the in-column
                copy renders transparent to avoid a double paint + a pattern
                seam at the column edge. */}
            <div style={{ ...baseStyle, ...((desktopMaxW && bgFullBleed) ? { background: 'transparent', backgroundImage: 'none' } : backgroundLayerStyle(s, p)) }} />
          </div>
        );
      }
      case 'icon': {
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <IconBox style={{
              background: s.bgTransparent ? 'transparent' : (s.bgColor || 'transparent'),
              borderRadius: s.borderRadius ? `${s.borderRadius}px` : undefined,
              padding: s.padding !== undefined ? `${s.padding}px` : undefined,
              opacity: s.opacity !== undefined ? s.opacity / 100 : undefined,
            }}>
              <IconGlyph
                source={p.iconSource || 'material'}
                name={p.iconName || 'favorite'}
                fill={p.iconFill !== false}
                weight={p.iconWeight || 500}
                color={s.textColor || '#2563eb'}
              />
            </IconBox>
          </div>
        );
      }
      case 'tabs': {
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, width: '100%', height: '100%' }}>
              <TabsBlock
                tabs={(p.tabs || []).map((t: any) => ({ ...t, label: rendererLocale === 'en' ? (t.labelEn || t.label) : t.label }))}
                activeTabId={activeTabIdFor(block as any, activeTabs)}
                onSelect={(tid) => setActiveTabs(m => ({ ...m, [block.id]: tid }))}
                activeColor={p.activeColor || '#2563eb'}
                inactiveColor={p.inactiveColor || '#6b7280'}
                barBg={p.barBg || 'transparent'}
                tabStyle={p.tabStyle || 'underline'}
                tabAlign={p.tabAlign || 'start'}
                tabRadius={typeof p.tabRadius === 'number' ? p.tabRadius : 999}
                underlineThickness={typeof p.underlineThickness === 'number' ? p.underlineThickness : 2}
                barLineColor={p.barLineColor || '#e5e7eb'}
                tabFontSize={p.tabFontSize || 15}
              />
            </div>
          </div>
        );
      }
      case 'grid': {
        // Grid container: just the cell frame. Members are separate absolutely-
        // positioned blocks snapped into the cells (rendered like any block).
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <div style={{ ...baseStyle, width: '100%', height: '100%' }}>
              <GridBlock
                cols={p.cols ?? 2}
                rows={p.rows ?? 2}
                gap={typeof p.gap === 'number' ? p.gap : 12}
                guides={!!p.showGuides}
                cellBg={p.cellBg}
                cellBorderColor={p.cellBorderColor}
                cellRadius={p.cellRadius}
              />
            </div>
          </div>
        );
      }
      case 'container': {
        const shadowMap: Record<string, string> = {
          none: 'none',
          sm: '0 1px 3px rgba(0,0,0,0.1)',
          md: '0 4px 12px rgba(0,0,0,0.1)',
          lg: '0 8px 30px rgba(0,0,0,0.12)',
          xl: '0 16px 48px rgba(0,0,0,0.15)',
        };
        // Grow the container to wrap any block the designer placed visually
        // on top of it (see containerChildren). Without this, when a child
        // has been shifted down by a dynamic sibling above the container, it
        // can end up below the container's authored bottom — visually leaving
        // the colored frame the designer built around it.
        const myBottom = layout.y + yOffset + layout.height;
        let neededBottom = myBottom;
        for (const childId of (containerChildren.get(block.id) || [])) {
          const child = blocks.find(b => b.id === childId);
          if (!child) continue;
          const childLayout = deviceMode === 'desktop' ? child.desktopLayout : child.mobileLayout;
          if (!childLayout) continue;
          const childOffset = getCumulativeOffset(childLayout.y, child.id);
          const childDelta = DYNAMIC_HEIGHT_TYPES.has(child.type) ? Math.max(0, blockDeltas[child.id] ?? 0) : 0;
          const childBottom = childLayout.y + childOffset + childLayout.height + childDelta;
          if (childBottom > neededBottom) neededBottom = childBottom;
        }
        const effectiveTop = layout.y + yOffset;
        const effectiveHeight = Math.max(layout.height, neededBottom - effectiveTop);
        return (
          <div key={block.id} data-block-id={block.id} style={{
            ...wrapStyle,
            height: effectiveHeight,
            minHeight: effectiveHeight,
            boxShadow: shadowMap[p.shadow || 'md'] || shadowMap.md,
            border: p.borderWidth ? `${p.borderWidth}px solid ${p.borderColor || '#e5e7eb'}` : undefined,
          }}>
            <div style={baseStyle}>
              {block.content && <div style={{ color: s.textColor || '#374151', fontSize: s.fontSize || 14 }}>{block.content}</div>}
            </div>
          </div>
        );
      }
      default:
        return (
          <div key={block.id} data-block-id={block.id} style={wrapStyle}>
            <div style={baseStyle}>{block.content || block.type}</div>
          </div>
        );
    }
  };

  // Helper to format relative time. Now locale-aware via i18next interpolation.
  // Hebrew keys preserve the legacy "לפני {{n}} דקות" wording byte-for-byte.
  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return t('public.recentDonors.now');
    if (minutes < 60) return t('public.recentDonors.minutesAgo', { n: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('public.recentDonors.hoursAgo', { n: hours });
    const days = Math.floor(hours / 24);
    return t('public.recentDonors.daysAgo', { n: days });
  };

  return (
    <div
      // pb on mobile only: the fixed PoweredByAmutech badge sits bottom-left
      // (~90px tall) on <640px, so add scroll clearance so it never covers the
      // footer/merchant disclosure. Desktop badge is top-left → no padding.
      className="min-h-screen bg-white pb-28 sm:pb-0"
      // Bilingual: wrapper direction follows the donor's locale so every
      // nested block inherits LTR for English. Hebrew (legacy) keeps RTL —
      // byte-identical to the pre-bilingual layout.
      dir={rendererLocale === 'en' ? 'ltr' : 'rtl'}
      style={{
        fontFamily: "'Heebo', sans-serif",
        overflowX: 'hidden',
        // touchAction: pan-y tells iOS Safari "this element only participates
        // in vertical pan (scroll)" — without it the browser waits to see
        // whether a touch over absolute-positioned, CSS-zoomed children is
        // a tap, pinch, or scroll, and scrolling intermittently stalls.
        touchAction: 'pan-y',
      }}
    >
      <PoweredByAmutech />

      {showDonationToasts && (
        <DonationToastStack
          items={donationToasts}
          primary={primary}
          corner={(campaign as any).donationToastPosition || 'bottom-left'}
          labels={{
            title: t('public.donationToasts.title'),
            donated: (name: string) => t('public.donationToasts.donated', { name }),
            via: (ambassador: string) => t('public.donationToasts.viaAmbassador', { ambassador }),
          }}
          onDismiss={dismissToast}
        />
      )}

      {/* Bilingual language toggle (block-based renderer) — only shown when
          the campaign owner opted in via bilingualEnabled + 'en' in
          supportedLanguages. Floating pill in the top-end corner so it
          doesn't interfere with the authored block layout. Clicking either
          option full-reloads with the new ?lang= so Emotion cache + theme
          rebuild at the right direction (see main.tsx).

          IMPORTANT: this lives inside CampaignBlocksRenderer because every
          live campaign goes through this path (PublicCampaign line 2693
          unconditionally returns this renderer). The legacy hero layout
          further down in the file is never executed. */}
      {/* Compact floating widget — combines language + currency in ONE small
          pill in the top-end corner. Each opens a tiny popover with options
          on click. Designed to stay small on mobile (≤ 100px wide) so it
          doesn't cover the campaign content. Falls back gracefully:
          - bilingual off → only currency button visible
          - single currency → only language button visible
          - both off → nothing rendered */}
      {(() => {
        const hasLang = !!campaign?.bilingualEnabled && (campaign.supportedLanguages || []).includes('en');
        const allowed: string[] = ((campaign as any)?.allowedCurrencies as string[]) || [];
        const hasCurrency = allowed.length > 1;
        if (!hasLang && !hasCurrency) return null;

        const activeLang = getBootLocale();
        const cellStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 36,
          height: 32,
          padding: '0 8px',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          background: 'transparent',
          border: 'none',
          color: '#374151',
          lineHeight: 1,
          fontFamily: "'Heebo', sans-serif",
          position: 'relative',
        };

        // Click-outside handler shared by both menus.
        // We don't need real state here — using native <details> is the
        // simplest path: it toggles open/closed on click, closes on
        // blur/escape, and works on touch out of the box. Per-element
        // `open` state is managed by the browser.

        return (
          <div
            dir="ltr"
            style={{
              position: 'fixed',
              top: 12,
              insetInlineEnd: 12,
              zIndex: 9500,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: 3,
              borderRadius: 8,
              background: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #e5e7eb',
            }}
          >
            {hasLang && (
              <details style={{ position: 'relative' }}>
                <summary style={{ ...cellStyle, listStyle: 'none' }}>
                  <span style={{ fontWeight: 800 }}>{activeLang === 'he' ? 'עב' : 'EN'}</span>
                  <span style={{ fontSize: 9, marginInlineStart: 2, color: '#9ca3af' }}>▼</span>
                </summary>
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    insetInlineEnd: 0,
                    minWidth: 100,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(['he', 'en'] as const).map((lng) => {
                    const active = activeLang === lng;
                    return (
                      <a
                        key={lng}
                        href={buildLocaleUrl(lng)}
                        style={{
                          padding: '8px 10px',
                          fontSize: 13,
                          fontWeight: active ? 800 : 600,
                          color: active ? '#fff' : '#374151',
                          background: active ? '#7c3aed' : 'transparent',
                          borderRadius: 4,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          textAlign: lng === 'he' ? 'right' : 'left',
                        }}
                      >
                        {lng === 'he' ? 'עברית' : 'English'}
                      </a>
                    );
                  })}
                </div>
              </details>
            )}

            {hasLang && hasCurrency && (
              <span style={{ width: 1, height: 18, background: '#e5e7eb' }} aria-hidden />
            )}

            {hasCurrency && (
              <details style={{ position: 'relative' }}>
                <summary style={{ ...cellStyle, listStyle: 'none' }}>
                  <span style={{ fontWeight: 800 }}>{currencySymbol(currency)}</span>
                  <span style={{ fontSize: 9, marginInlineStart: 2, color: '#9ca3af' }}>▼</span>
                </summary>
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    insetInlineEnd: 0,
                    minWidth: 100,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {allowed.map((c) => {
                    const active = currency === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={(e) => {
                          setCurrency(c as any);
                          // Close the <details> popover by removing its open attribute.
                          const det = (e.currentTarget.closest('details') as HTMLDetailsElement | null);
                          if (det) det.open = false;
                        }}
                        style={{
                          padding: '8px 10px',
                          fontSize: 13,
                          fontWeight: active ? 800 : 600,
                          color: active ? '#fff' : '#374151',
                          background: active ? '#2563eb' : 'transparent',
                          borderRadius: 4,
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ minWidth: 14, fontWeight: 800 }}>{currencySymbol(c)}</span>
                        <span>{c}</span>
                      </button>
                    );
                  })}
                </div>
              </details>
            )}
          </div>
        );
      })()}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        ${ANIMATION_KEYFRAMES}
      `}</style>

      {/* Outer wrapper measures the available width. The inner canvas renders
          blocks at their authored pixel coordinates and is uniformly scaled
          by a single transform — so the public page is a literal zoom of the
          editor preview at any viewport width. */}
      {(() => {
        const innerCanvasHeight = canvasHeight;
        return (
          <div ref={containerRef} style={{
            // OUTER: always full viewport width. It measures the available
            // width AND paints the page background — either the full-bleed
            // campaign background (edge-to-edge behind the side margins) or the
            // page/margin colour when the background is boxed. When no max
            // width is set this is just the white backdrop, exactly as before.
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            position: 'relative',
            height: innerCanvasHeight * ratio,
            overflow: 'visible',
            ...((desktopMaxW && bgFullBleed)
              ? backgroundLayerStyle(bgLayoutStyle as any, bgLayoutProps)
              : { background: desktopMaxW ? pageColor : '#fff' }),
            // iOS Safari auto-inflates text font-size based on the real
            // viewport (default `-webkit-text-size-adjust: auto`), independent
            // of our `zoom`-scaled canvas. A wider phone gets LESS inflation,
            // so the same text block wraps to FEWER lines than on a narrow
            // phone (and fewer than the editor preview, which never inflates)
            // — its box then underfills and a gap opens before the next block.
            // Pinning to 100% disables inflation so every device renders the
            // un-inflated baseline = the editor, and wrapping is identical
            // everywhere. This is the real root of the device-dependent gaps.
            WebkitTextSizeAdjust: '100%',
            textSizeAdjust: '100%',
          } as React.CSSProperties}>
            {/* COLUMN: the content width. Equals the viewport when no max is
                set (legacy), otherwise the capped width, centred via margin. */}
            <div style={{
              width: renderWidth,
              maxWidth: '100%',
              margin: '0 auto',
              position: 'relative',
              height: innerCanvasHeight * ratio,
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: canvasWidth,
                height: innerCanvasHeight,
                zoom: ratio,
                WebkitTextSizeAdjust: '100%',
                textSizeAdjust: '100%',
              } as React.CSSProperties}>
                {blocks.filter(b => !isHiddenByTab(b as any, blocks as any, activeTabs)).map(block => (
                  <BlockSlot
                    key={block.id}
                    block={block}
                    deviceMode={deviceMode}
                    primary={primary}
                    renderBlock={renderBlock}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })()}
      <PaymentIframeModal
        url={paymentUrl}
        open={paymentModalOpen}
        onClose={() => { setPaymentModalOpen(false); setBitDirect(false); }}
        onSuccess={handlePaymentSuccess}
        onFail={handlePaymentFail}
        bitContext={bitContext}
        startInBit={bitDirect}
      />

      {/* Native payment-method picker — only when the campaign opted in.
          Credit → the iframe flow above; bit → the dedicated bit flow;
          Apple/Google → native Hosted Fields (donation created via callback). */}
      <PaymentMethodPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        amount={getAmount()}
        currency={currency}
        campaignName={campaign?.name || ''}
        enableBit={campaign?.pickerEnableBit !== false}
        enableGooglePay={campaign?.pickerEnableGooglePay !== false}
        enableApplePay={campaign?.pickerEnableApplePay !== false}
        bitAvailable={bitButtonAvailable}
        onSelectCredit={handleDonate}
        onSelectBit={handleBitDonate}
        requestWalletCharge={requestWalletCharge}
        onWalletSuccess={handlePaymentSuccess}
        onWalletError={(msg) => { setPickerOpen(false); setDonationError(msg); }}
      />

      {/* External payment surfaces — נדרים פלוס / jGive (per-campaign).
          Nedarim uses the PostNedarim card-iframe: our form collects everything
          (incl. custom fields), Nedarim's iframe takes only card + ת"ז, and the
          donation is recorded by the webhook. Groupe carries
          "<campaign> | <receipt text>"; custom fields ride a PaymentSession. */}
      {campaign?.nedarimIframe?.mosadId && (
        <NedarimCardModal
          open={nedarimModalOpen}
          onClose={() => setNedarimModalOpen(false)}
          mosadId={campaign.nedarimIframe.mosadId}
          apiValid={campaign.nedarimIframe.apiValid || ''}
          campaignId={campaign?._id || ''}
          campaignName={campaign?.name || ''}
          receiptMessage={campaign?.receiptMessage || ''}
          amount={getAmount()}
          currency={currency === 'USD' ? 'USD' : 'ILS'}
          isRecurring={isRecurring}
          recurringMonths={recurringMonths}
          firstName={getNameParts().firstName}
          lastName={getNameParts().lastName}
          phone={donorPhone}
          email={donorEmail}
          ambassadorSlug={ambassadorRef}
          customFields={Object.keys(customFieldValues).length ? customFieldValues : undefined}
          onSuccess={handlePaymentSuccess}
          onFail={handlePaymentFail}
        />
      )}
      <ExternalIframeModal
        open={externalJgiveOpen}
        url={campaign?.externalPayment?.jgiveUrl || ''}
        title="תרומה דרך jGive"
        onClose={() => setExternalJgiveOpen(false)}
      />

      {/* external-payment block in 'modal' display mode — Nedarim's hosted
          online page / jGive / manual customUrl in the dumb iframe host. */}
      <ExternalIframeModal
        open={!!extBlockModal}
        url={extBlockModal?.url || ''}
        title={extBlockModal?.title || ''}
        onClose={() => setExtBlockModal(null)}
      />

      {/* ─── Footer — transparent background so it blends with whatever
            theme the nonprofit set for their campaign page. Uses muted
            neutral tones that read on light backgrounds (the default for
            campaign body) but stay unobtrusive. ────────────────────────── */}
      <footer
        dir={rendererLocale === 'en' ? 'ltr' : 'rtl'}
        className="mt-8 md:mt-12 text-gray-600"
        style={{ fontFamily: "'Heebo', sans-serif" }}
      >
        <div className="max-w-3xl mx-auto px-4 md:px-5 py-5 md:py-8 border-t border-gray-200/70">
          {/* Nonprofit identity — primary content */}
          <div className="text-center mb-4 md:mb-5">
            <h3 className="text-gray-800 font-bold text-base md:text-lg mb-2">
              {campaign.orgName}
            </h3>
            <div className="text-xs md:text-sm text-gray-600 leading-relaxed space-y-0.5">
              {campaign.orgRegistrationNumber && (
                <div>{t('public.footer.orgRegistrationNumber')}: {campaign.orgRegistrationNumber}</div>
              )}
              {campaign.orgAddress && <div>{campaign.orgAddress}</div>}
              {(campaign.orgPhone || campaign.orgEmail) && (
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
                  {campaign.orgPhone && (
                    <a
                      href={`tel:${campaign.orgPhone}`}
                      className="hover:text-gray-900 transition-colors underline-offset-2 hover:underline"
                      dir="ltr"
                    >
                      {campaign.orgPhone}
                    </a>
                  )}
                  {campaign.orgPhone && campaign.orgEmail && (
                    <span className="text-gray-400">·</span>
                  )}
                  {campaign.orgEmail && (
                    <a
                      href={`mailto:${campaign.orgEmail}`}
                      className="hover:text-gray-900 transition-colors underline-offset-2 hover:underline"
                      dir="ltr"
                    >
                      {campaign.orgEmail}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Legal links row */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs md:text-sm pb-4 border-b border-gray-200/60">
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900 transition-colors"
            >
              {t('public.payment.termsLink')}
            </a>
            <span className="text-gray-400">·</span>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900 transition-colors"
            >
              {t('public.payment.privacyLink')}
            </a>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{t('public.footer.securePayment')}</span>
          </div>

          {/* Powered-by Amutech (small) + merchant disclosure */}
          <div className="pt-3 md:pt-4 flex flex-col items-center gap-2 text-center">
            <a
              href="https://amutech.co.il"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] md:text-xs text-gray-500 hover:text-gray-800 transition-colors"
            >
              <img
                src="/amutech-logo.png"
                alt="עמוטק"
                className="h-4 w-auto object-contain opacity-70"
              />
              <span>מופעל באמצעות עמוטק</span>
            </a>
            <p className="text-[10px] md:text-[11px] text-gray-500 leading-relaxed max-w-md">
              סליקה מבוצעת ע&quot;י Y.Sh כלכלה וצריכה חברתית בע&quot;מ
              (ח.פ. 516866514, עציון 27 פרדס חנה) — סוחר רשום ב-Tranzila / HyP, תקן PCI-DSS
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const PublicCampaign: React.FC = () => {
  const { t } = useTranslation();
  // Outer-component locale: in scope for the loading/error/paused early-return
  // screens. CampaignBlocksRenderer reads its own rendererLocale.
  const outerLocale = getBootLocale();
  const outerDir = outerLocale === 'en' ? 'ltr' : 'rtl';
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const ambassadorRef = searchParams.get('ref');
  // Mail-campaign attribution: the link a donor clicks from email/SMS/WhatsApp
  // carries `?mc=<mailCampaignId>` (set by services/mailSender.ts + the WA
  // send path). Thread it through to payment-url + the direct-card POST so
  // the resulting Donation gets stamped — the Income table reads this to
  // render the "דיוור" column.
  const mailCampaignId = searchParams.get('mc');

  const [campaign, setCampaign] = useState<PublicCampaignData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedAmount, setSelectedAmount] = useState<number | null>(200);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  // ── Multi-currency: donor's selected currency ─────────────────────────
  // Initialized from the campaign's defaultCurrency once it loads. When
  // allowedCurrencies.length === 1 the toggle UI is hidden entirely, so
  // every legacy single-currency campaign behaves byte-identically.
  const [currency, setCurrency] = useState<'ILS' | 'USD' | 'EUR' | 'GBP'>('ILS');
  const [donating] = useState(false);
  const [donationSuccess, setDonationSuccess] = useState(false);
  const [donationError, setDonationError] = useState<string | null>(null);
  const [receiptNumber, setReceiptNumber] = useState<string | null>(null);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [_paymentMethod, setPaymentMethod] = useState<'card' | 'bit'>('card');

  // Per-org Bit availability — probed once the campaign is loaded.
  // Hidden by default until the probe responds; org admin enables Bit by
  // entering merchant credentials in /admin/organizations/:id/terminals.
  const [bitAvailable, setBitAvailable] = useState(false);
  const [bitModalOpen, setBitModalOpen] = useState(false);
  const [bitInitiateBody, setBitInitiateBody] = useState<Record<string, any> | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringMonths, setRecurringMonths] = useState(12);
  const [recurringConsent, setRecurringConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  // UI state
  const [progressWidth, setProgressWidth] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resolvedAmbassador, setResolvedAmbassador] = useState<{ name: string; slug: string } | null>(null);
  const [showAllDonors, setShowAllDonors] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicInteracted, setMusicInteracted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const primary = campaign?.primaryColor || '#1565C0';
  const primaryLight = lightenHex(primary, 0.4);

  // Bilingual: active locale is decided once at boot from ?lang= (see
  // main.tsx + i18n/config.ts). All campaign-level text reads go through
  // these memoized resolvers — they fall back to the legacy single-string
  // fields when no bilingual data exists, so legacy campaigns are unaffected.
  const activeLocale = getBootLocale();
  const dispName = useMemo(
    () => resolveStr(campaign?.nameI18n, activeLocale) || campaign?.name || '',
    [campaign?.nameI18n, campaign?.name, activeLocale]
  );
  const dispDescription = useMemo(
    () => resolveStr(campaign?.descriptionI18n, activeLocale) || campaign?.description || '',
    [campaign?.descriptionI18n, campaign?.description, activeLocale]
  );
  const dispAiDescription = useMemo(
    () => resolveStr(campaign?.aiDescriptionI18n, activeLocale) || campaign?.aiDescription || '',
    [campaign?.aiDescriptionI18n, campaign?.aiDescription, activeLocale]
  );
  const dispImageUrl = useMemo(
    () => resolve<string>(campaign?.imageUrlI18n, activeLocale) || campaign?.imageUrl || '',
    [campaign?.imageUrlI18n, campaign?.imageUrl, activeLocale]
  );

  // Scroll listener for sticky bar
  useEffect(() => {
    const onScroll = () => {
      const heroBottom = heroRef.current?.getBoundingClientRect().bottom ?? 0;
      setShowStickyBar(heroBottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        setLoading(true);
        let data: PublicCampaignData;
        if (ambassadorRef) {
          try {
            const r = await axios.get(`${API_BASE}/public/campaigns/${slug}/ambassador/${ambassadorRef}`);
            data = r.data;
          } catch {
            const r = await axios.get(`${API_BASE}/public/campaigns/${slug}`);
            data = r.data;
          }
        } else {
          const r = await axios.get(`${API_BASE}/public/campaigns/${slug}`);
          data = r.data;
        }
        setCampaign(data);
        // Multi-currency: anchor the donor's initial currency to the campaign's
        // default. Single-currency campaigns (the legacy case) carry
        // defaultCurrency='ILS' and allowedCurrencies=['ILS'], so the donor
        // starts on ILS exactly like before — no UI changes visible.
        if (data?.defaultCurrency) {
          setCurrency(data.defaultCurrency as any);
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || 'הקמפיין לא נמצא');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!campaign) return;
    const pct = Math.min((campaign.raised / campaign.goal) * 100, 100);
    const t = setTimeout(() => setProgressWidth(pct), 400);
    return () => clearTimeout(t);
  }, [campaign]);

  useEffect(() => {
    if (!campaign || !ambassadorRef) return;
    const amb = campaign.ambassadors?.find(a => a.slug === ambassadorRef);
    // Bilingual: prefer ambassador's per-locale display name when set,
    // otherwise the legacy single-string `name` (which is what every
    // existing ambassador has today).
    if (amb) {
      const localizedName = resolveStr(amb.nameI18n, activeLocale) || amb.name;
      setResolvedAmbassador({ name: localizedName, slug: amb.slug });
    } else if (campaign.ambassadorContext) {
      const ctx = campaign.ambassadorContext;
      const localizedName = resolveStr(ctx.nameI18n, activeLocale) || ctx.name;
      setResolvedAmbassador({ name: localizedName, slug: ctx.slug });
    }
  }, [campaign, ambassadorRef, activeLocale]);

  // Track page visit
  useEffect(() => {
    if (!campaign || !slug) return;
    const src = searchParams.get('src');
    const waToken = searchParams.get('wa');
    let source = 'other';
    if (ambassadorRef) source = 'ambassador';
    else if (waToken) source = 'whatsapp';
    else if (src === 'sms') source = 'sms';
    else if (src === 'whatsapp') source = 'whatsapp';
    else if (src === 'email') source = 'email';
    axios.post(`${API_BASE}/public/campaigns/${slug}/visit`, {
      source,
      ambassadorSlug: ambassadorRef || '',
    }).catch(() => {});
    // If we arrived from a WhatsApp template button (`?wa=<jwt>`), tell
    // the backend so the mailing campaign's totalCampaignClicks +
    // clickedContactIds get updated. Idempotent per (campaign, contact).
    if (waToken) {
      axios.post(`${API_BASE}/public/wa-click`, { token: waToken }).catch(() => {});
    }
  }, [campaign, slug]);

  // Probe whether this org has Bit/GamaPay configured. The endpoint is
  // public — falls back to {bit:false} on error so the button stays hidden.
  useEffect(() => {
    if (!campaign?.orgId) { setBitAvailable(false); return; }
    let alive = true;
    axios.get(`${API_BASE}/payments/providers-available`, { params: { orgId: campaign.orgId } })
      .then(r => { if (alive) setBitAvailable(!!r.data?.bit); })
      .catch(() => { if (alive) setBitAvailable(false); });
    return () => { alive = false; };
  }, [campaign?.orgId]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.status === 'success' || e.data?.Response === '000') {
        setShowIframe(false);
        setDonationSuccess(true);
        setReceiptNumber(e.data?.receiptNumber || null);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 4000);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // ── Music: start on first interaction ──
  useEffect(() => {
    if (!campaign?.backgroundMusicUrl) return;
    const startMusic = () => {
      if (musicInteracted) return;
      setMusicInteracted(true);
      if (audioRef.current) {
        audioRef.current.volume = 0.4;
        audioRef.current.play().then(() => {
          setMusicPlaying(true);
        }).catch(() => {
          // autoplay blocked, user can click manually
        });
      }
    };
    document.addEventListener('click', startMusic, { once: true });
    return () => document.removeEventListener('click', startMusic);
  }, [campaign?.backgroundMusicUrl, musicInteracted]);

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (musicPlaying) {
      audioRef.current.pause();
      setMusicPlaying(false);
    } else {
      audioRef.current.volume = 0.4;
      audioRef.current.play().then(() => setMusicPlaying(true)).catch(() => {});
      setMusicInteracted(true);
    }
  };

  const getAmount = () => customAmount ? parseFloat(customAmount) : (selectedAmount || 0);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Method-chooser dialog: shown when bit is available + not recurring,
  // letting the donor pick between Bit and credit card. If recurring or
  // no Bit, we go straight to the credit card iframe.
  const [methodChoiceOpen, setMethodChoiceOpen] = useState(false);

  // External payment iframes (נדרים פלוס / jGive) — configured per campaign in
  // the builder; donations land via the provider's webhook, not our form.
  const [nedarimModalOpen, setNedarimModalOpen] = useState(false);
  const [externalJgiveOpen, setExternalJgiveOpen] = useState(false);

  const openExternalPayment = (): boolean => {
    const ext = campaign?.externalPayment;
    if (!ext?.provider) return false;
    if (ext.provider === 'nedarim' && campaign?.nedarimIframe?.mosadId) {
      setNedarimModalOpen(true);
      return true;
    }
    if (ext.provider === 'jgive' && ext.jgiveUrl) {
      setExternalJgiveOpen(true);
      return true;
    }
    return false;
  };

  const startDonate = () => {
    const amount = getAmount();
    if (!amount || amount <= 0) { setDonationError('אנא בחר סכום תרומה'); return; }
    if (!donorName.trim()) { setDonationError('אנא הזן שם'); return; }
    if (isRecurring && !recurringConsent) {
      setDonationError('עליך לאשר את תנאי הוראת הקבע כדי להמשיך');
      return;
    }
    if (!termsAccepted) {
      setDonationError('יש לאשר את התקנון ומדיניות הפרטיות לפני ביצוע התשלום');
      return;
    }
    setDonationError(null);
    // External payment iframe replaces the built-in flow (mode 'alongside'
    // keeps this button on the built-in flow; a secondary button opens the
    // external one).
    if (campaign?.externalPayment?.mode !== 'alongside' && openExternalPayment()) return;
    // Bit (GamaPay) is ILS-only — the gateway has no USD/EUR/GBP support.
    // For non-ILS donations, skip the method picker and go straight to card.
    if (bitAvailable && !isRecurring && currency === 'ILS') {
      setMethodChoiceOpen(true);
    } else {
      handleDonate('card');
    }
  };

  const handleDonate = async (method: 'card' | 'bit') => {
    const amount = getAmount();
    if (!amount || amount <= 0) { setDonationError('אנא בחר סכום תרומה'); return; }
    if (!donorName.trim()) { setDonationError('אנא הזן שם'); return; }
    if (isRecurring && !recurringConsent) {
      setDonationError('עליך לאשר את תנאי הוראת הקבע כדי להמשיך');
      return;
    }
    if (!termsAccepted) {
      setDonationError('יש לאשר את התקנון ומדיניות הפרטיות לפני ביצוע התשלום');
      return;
    }
    setDonationError(null);
    setPaymentMethod(method);

    // BIT path — open the Bit modal which collects phone + sends SMS link.
    // Recurring is NOT supported via Bit (no stored token); we silently
    // fall through to card here if the donor toggled monthly + bit.
    if (method === 'bit' && bitAvailable && !isRecurring) {
      setBitInitiateBody({
        orgId: campaign?.orgId || '',
        amount,
        campaignId: campaign?._id || '',
        ambassadorSlug: resolvedAmbassador?.slug || '',
        donorName,
        donorEmail: donorEmail || '',
        ...(mailCampaignId ? { mailCampaignId } : {}),
        // donorPhone is collected inside the modal
      });
      setBitModalOpen(true);
      return;
    }

    setPaymentLoading(true);

    try {
      // Use HypPay via the org's terminal
      const params = new URLSearchParams({
        amount: String(amount),
        orgId: campaign?.orgId || '',
        campaignId: campaign?._id || '',
        clientName: donorName,
        clientEmail: donorEmail || '',
        clientPhone: '',
        redirect: 'false',
        ...(isRecurring ? { recurring: 'true', recurringMonths: String(recurringMonths) } : {}),
        ...(mailCampaignId ? { mailCampaignId } : {}),
        // Donor locale → Tranzila lang + Ilang (see the other payment-url
        // call site above for the contract). Defaults to Hebrew.
        lang: getBootLocale(),
        // Forward custom-field answers so the backend can render
        // {שדה.LABEL} placeholders before building the iframe URL.
        ...(Object.keys(customFieldValues).length > 0
          ? { customFields: JSON.stringify(customFieldValues) }
          : {}),
      });

      const { data } = await axios.get(`${API_BASE}/public/payment-url?${params}`);

      if (data.url) {
        try {
          if (Object.keys(customFieldValues).length > 0) {
            sessionStorage.setItem('pendingCustomFields', JSON.stringify(customFieldValues));
          }
        } catch {}
        setPaymentUrl(data.url);
        setPaymentModalOpen(true);
      } else {
        setDonationError('שגיאה ביצירת לינק תשלום');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'שגיאה ביצירת תשלום';
      setDonationError(msg);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSuccess = (payload: Record<string, string>) => {
    setPaymentModalOpen(false);
    const qs = new URLSearchParams({ ...payload, campaignSlug: campaign?.slug || '' }).toString();
    window.location.href = `/payments/complete?${qs}`;
  };

  const handlePaymentFail = (payload: Record<string, string>) => {
    setPaymentModalOpen(false);
    setDonationError(payload?.error || t('public.errors.paymentNotCompleted'));
  };

  const handleShare = () => {
    const url = window.location.href;
    window.open(`https://wa.me/?text=${encodeURIComponent(`תרמתי לקמפיין "${campaign?.name}" — הצטרפו גם! 🎉 ${url}`)}`, '_blank');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Loading ──────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir={outerDir}>
      <div className="text-center">
        <div className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin mx-auto mb-5" />
        <p className="text-gray-500 text-lg font-medium">{t('public.states.loading')}</p>
      </div>
    </div>
  );

  if (error || !campaign) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir={outerDir}>
      <div className="text-center p-10 bg-white rounded-3xl shadow-xl max-w-sm mx-4">
        <p className="text-6xl mb-4">😔</p>
        <p className="text-gray-600 text-lg font-medium">{error || t('public.states.notFound')}</p>
      </div>
    </div>
  );

  // ─── Paused/Draft campaign ─────────────────────────────────────────────────
  if ((campaign as any).paused) return (
    <div className="min-h-screen flex items-center justify-center" dir={outerDir} style={{ background: 'linear-gradient(135deg, #fefce8, #fef9c3)' }}>
      <div className="text-center p-12 bg-white rounded-3xl shadow-xl max-w-md mx-4">
        <p className="text-7xl mb-6">⏸️</p>
        <h1 className="text-2xl font-black text-gray-800 mb-3">{t('public.states.pausedTitle')}</h1>
        <p className="text-gray-500 text-lg mb-2">{t('public.states.pausedBody', { name: campaign.name })}</p>
        {(campaign as any).orgName && <p className="text-gray-400 text-sm">{(campaign as any).orgName}</p>}
        <a href="/" className="inline-block mt-6 px-6 py-3 bg-gray-800 text-white font-bold rounded-xl text-sm hover:bg-gray-700 transition-colors">{t('public.states.homeLink')}</a>
      </div>
    </div>
  );

  // ─── Always use new blocks-based renderer ──────────────────────────────────
  return <CampaignBlocksRenderer campaign={campaign} primary={primary} ambassadorRef={ambassadorRef} mailCampaignId={mailCampaignId} />;

  // ─── Legacy renderer (kept for reference, never reached) ──────────────────

  const progress = Math.min((campaign.raised / campaign.goal) * 100, 100);
  const daysLeft = Math.max(0, Math.ceil((new Date(campaign.endDate).getTime() - Date.now()) / 86400000));
  const donorCount = Math.round(campaign.raised / 150);
  const visibleDonors = showAllDonors ? FAKE_DONORS : FAKE_DONORS.slice(0, 6);
  const isTrending = progress >= 30;

  // ─── Donation form ────────────────────────────────────────────────────────

  const DonationForm = () => (
    <div ref={formRef} className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {donationSuccess ? (
        <div className="p-8 text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-pulse"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">✅ תודה על תרומתך!</h2>
          <p className="text-base text-gray-600 mb-2">אתה תרמת לקמפיין <strong className="text-gray-800">{campaign.name}</strong></p>
          <p className="text-gray-600 mb-4">תרומתך של <strong style={{ color: primary }} className="text-lg">{currencySymbol(currency)}{getAmount().toLocaleString()}</strong> התקבלה בהצלחה</p>
          {receiptNumber && (
            <p className="text-xs text-gray-500 mt-3 mb-6 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
              מספר קבלה: <strong className="text-gray-700 text-sm">{receiptNumber}</strong>
            </p>
          )}
          <div className="space-y-3 mt-6">
            <button onClick={handleShare}
              className="w-full py-3.5 rounded-2xl font-bold text-white transition-all hover:brightness-110 flex items-center justify-center gap-2 shadow-md"
              style={{ background: '#25D366' }}>
              📲 שתף בוואטסאפ
            </button>
            <button onClick={handleCopy}
              className="w-full py-3.5 rounded-2xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all">
              {copied ? '✅ הקישור הועתק!' : '🔗 העתק קישור'}
            </button>
            <button onClick={() => window.location.href = '/'}
              className="w-full py-3 rounded-2xl font-semibold text-gray-700 border-2 border-gray-300 hover:bg-gray-50 transition-all">
              ← חזור לעמוד הבית
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Form header */}
          <div className="px-6 pt-6 pb-5 border-b border-gray-100">
            <h2 className="text-xl font-black text-gray-800 mb-1">בחר סכום תרומה</h2>
            <p className="text-gray-400 text-sm">כל שקל מקרב אותנו ליעד 🙏</p>
          </div>

          <div className="px-6 pb-6 space-y-6 pt-5">

            {/* ── Section 46 Banner ── */}
            {campaign.section46 && (
              <div className="rounded-2xl px-4 py-3 flex items-center gap-2 text-center justify-center"
                style={{ background: '#dcfce7', border: '1px solid #86efac' }}>
                <span className="text-lg">✅</span>
                <p className="text-green-800 font-semibold text-sm leading-snug">
                  התרומה מוכרת למס לפי סעיף 46 לפקודת מס הכנסה
                </p>
              </div>
            )}

            {/* ── Milestone tabs ── */}
            {(campaign.donationTiers?.length || 0) > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
                {campaign.donationTiers!.map((tier, i) => {
                  const sel = selectedAmount === tier.amount && !customAmount;
                  return (
                    <button
                      key={tier._id || i}
                      type="button"
                      onClick={() => { setSelectedAmount(tier.amount); setCustomAmount(''); }}
                      className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all whitespace-nowrap"
                      style={sel
                        ? { background: primary, borderColor: primary, color: '#fff' }
                        : { background: '#fff', borderColor: '#E5E7EB', color: '#374151' }
                      }
                    >
                      ₪{tier.amount.toLocaleString()}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Donation tiers grid ── */}
            {(campaign.donationTiers?.length || 0) > 0 ? (
              <div>
                <div className="grid grid-cols-2 gap-3 rounded-2xl border border-gray-200 overflow-hidden p-0.5 bg-gray-50">
                  {campaign.donationTiers!.map((tier, i) => {
                    const sel = selectedAmount === tier.amount && !customAmount;
                    const isPopular = i === Math.floor((campaign.donationTiers!.length - 1) / 2);
                    return (
                      <button
                        key={tier._id || i}
                        type="button"
                        onClick={() => {
                          setSelectedAmount(tier.amount);
                          setCustomAmount('');
                          if (tier.isRecurring && campaign.recurringEnabled) setIsRecurring(true);
                        }}
                        className="relative text-right p-4 rounded-xl border-2 transition-all duration-200 bg-white hover:border-gray-300"
                        style={sel
                          ? { borderColor: primary, background: '#ffffff', boxShadow: `0 0 0 3px ${primary}33, 0 4px 12px rgba(0,0,0,0.10)` }
                          : { borderColor: '#E5E7EB' }
                        }
                      >
                        {isPopular && (
                          <span className="absolute -top-px -right-px text-white text-xs font-bold px-2.5 py-0.5 rounded-tr-xl rounded-bl-xl"
                            style={{ background: primary }}>
                            פופולרי ★
                          </span>
                        )}
                        {sel && (
                          <span className="absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ background: primary }}>
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        )}
                        <div className="text-2xl font-black text-gray-800">₪{tier.amount.toLocaleString()}</div>
                        <div className="text-sm font-semibold text-gray-600 mt-0.5">{tier.name}</div>
                        {tier.description && (
                          <div className="text-gray-400 text-xs mt-1 leading-snug border-t border-gray-100 pt-1">{tier.description}</div>
                        )}
                        <div className="mt-1 text-gray-400" style={{ fontSize: 10, fontWeight: 500 }}>
                          {tier.isRecurring
                            ? `תשלום קבוע למשך ${(tier as any).recurringMonths || 12} חודשים`
                            : 'חד פעמי'}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Custom amount */}
                <div className="mt-3 flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-blue-400 transition-all bg-white">
                  <div className="px-4 py-3 bg-gray-50 border-l border-gray-200 text-gray-500 font-bold text-sm select-none">₪ ILS</div>
                  <input
                    type="number"
                    aria-label="סכום תרומה אחר בשקלים"
                    placeholder="סכום אחר..."
                    value={customAmount}
                    onChange={e => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                    min="1"
                    className="flex-1 px-4 py-3 bg-white text-gray-800 placeholder-gray-400 focus:outline-none text-right text-base"
                  />
                </div>
              </div>
            ) : (
              /* Default amount buttons */
              <div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {AMOUNT_OPTIONS.map(amt => {
                    const sel = selectedAmount === amt && !customAmount;
                    const popular = amt === 200;
                    return (
                      <div key={amt} className="relative">
                        {popular && (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-2.5 py-0.5 rounded-full z-10 whitespace-nowrap shadow-sm"
                            style={{ background: primary }}>
                            הכי פופולרי
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                          className={`w-full py-4 rounded-xl font-bold text-base border-2 transition-all duration-200 ${popular ? 'mt-1' : ''} ${sel ? 'text-white shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                          style={sel ? { background: primary, borderColor: primary } : {}}
                        >
                          ₪{amt}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-blue-400 transition-all bg-white">
                  <div className="px-4 py-3 bg-gray-50 border-l border-gray-200 text-gray-500 font-bold text-sm select-none">₪ ILS</div>
                  <input
                    type="number"
                    aria-label="סכום תרומה אחר בשקלים"
                    placeholder="סכום אחר..."
                    value={customAmount}
                    onChange={e => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                    min="1"
                    className="flex-1 px-4 py-3 bg-white text-gray-800 placeholder-gray-400 focus:outline-none text-right text-base"
                  />
                </div>
              </div>
            )}

            {/* ── Donor info ── */}
            <div className="space-y-3">
              <div>
                <label className="block">
                  <span className="block text-xs font-bold text-gray-500 mb-1.5 tracking-wide">שם מלא *</span>
                  <input
                    type="text"
                    value={donorName}
                    onChange={e => { setDonorName(e.target.value); if (donationError === 'אנא הזן שם') setDonationError(null); }}
                    placeholder="ישראל ישראלי"
                    required
                    className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 text-gray-800 placeholder-gray-400 rounded-xl focus:outline-none focus:border-blue-400 transition-all"
                  />
                </label>
              </div>
              <div>
                <label className="block">
                  <span className="block text-xs font-bold text-gray-500 mb-1.5 tracking-wide">אימייל (לקבלה)</span>
                  <input
                    type="email"
                    value={donorEmail}
                    onChange={e => setDonorEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 text-gray-800 placeholder-gray-400 rounded-xl focus:outline-none focus:border-blue-400 transition-all"
                  />
                </label>
              </div>
            </div>

            {/* ── Custom fields ── */}
            {(campaign.customFields?.length || 0) > 0 && (
              <div className="space-y-3">
                {campaign.customFields!.map(field => (
                  <div key={field.id}>
                    {field.type !== 'checkbox' && (
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">{field.label}{field.required && ' *'}</label>
                    )}
                    <CustomFieldInput field={field} value={customFieldValues[field.id]}
                      onChange={v => setCustomFieldValues(p => ({ ...p, [field.id]: v }))} />
                  </div>
                ))}
              </div>
            )}

            {/* ── Recurring — checkbox style ── */}
            {campaign.recurringEnabled !== false && (
              <div className="space-y-3 bg-gray-50 rounded-2xl p-4 border border-gray-200">
                {/* One-time */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                    style={!isRecurring ? { borderColor: primary, background: primary } : { borderColor: '#D1D5DB', background: '#fff' }}
                    onClick={() => setIsRecurring(false)}
                  >
                    {!isRecurring && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <span className={`text-sm font-semibold ${!isRecurring ? 'text-gray-800' : 'text-gray-500'}`}>
                    תרומה חד פעמית
                  </span>
                </label>

                {/* Recurring */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div
                    className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                    style={isRecurring ? { borderColor: primary, background: primary } : { borderColor: '#D1D5DB', background: '#fff' }}
                    onClick={() => setIsRecurring(true)}
                  >
                    {isRecurring && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${isRecurring ? 'text-gray-800' : 'text-gray-500'}`}>
                        הוראת קבע בסכום זה למשך
                      </span>
                      <select
                        value={recurringMonths}
                        onChange={e => { setRecurringMonths(Number(e.target.value)); setIsRecurring(true); }}
                        className="border border-gray-300 rounded-lg px-2 py-1 text-sm font-bold text-gray-700 bg-white focus:outline-none focus:border-blue-400"
                      >
                        {RECURRING_MONTHS_OPTIONS.map(m => (
                          <option key={m} value={m}>{m} חודשים</option>
                        ))}
                      </select>
                    </div>
                    {isRecurring && getAmount() > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        סה"כ: ₪{(getAmount() * recurringMonths).toLocaleString('he-IL')} לאורך {recurringMonths} חודשים
                      </p>
                    )}
                  </div>
                </label>

                {/* Recurring consent — legal disclosure + required checkbox */}
                {isRecurring && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <p className="text-xs text-amber-900 leading-relaxed">
                      <strong>תשומת לב:</strong> בתרומת הוראת קבע, כרטיס האשראי שלך יחויב אוטומטית בסכום של
                      <strong> ₪{getAmount().toLocaleString('he-IL')}</strong> בכל חודש, במשך <strong>{recurringMonths} חודשים</strong>.
                      פרטי הכרטיס יישמרו בצורה מוצפנת לצורך החיובים החודשיים.
                      ניתן לבטל את ההוראה בכל רגע ע"י פנייה לעמותה.
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={recurringConsent}
                        onChange={(e) => setRecurringConsent(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-amber-600"
                      />
                      <span className="text-xs font-semibold text-amber-900">
                        אני מאשר/ת את תנאי הוראת הקבע ושמירת פרטי האשראי
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* ── Terms-of-service acceptance — required for credit-card clearing compliance ── */}
            <label className="flex items-start gap-2 cursor-pointer px-1">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0"
                style={{ accentColor: primary }}
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                קראתי ואני מאשר/ת את{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:opacity-80"
                  style={{ color: primary }}
                >
                  התקנון
                </a>{' '}
                ואת{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:opacity-80"
                  style={{ color: primary }}
                >
                  מדיניות הפרטיות
                </a>
              </span>
            </label>

            {/* ── Payment loading ── */}
            {paymentLoading && (
              <div className="rounded-2xl border-2 border-blue-100 bg-blue-50 p-8 text-center">
                <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-blue-700 font-semibold">מעביר לדף תשלום מאובטח...</p>
              </div>
            )}

            {donationError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                <span className="text-red-500">⚠️</span>
                <p className="text-red-600 text-sm">{donationError}</p>
              </div>
            )}

            {/* ── Single donate button — opens method chooser when Bit is
                 available + not recurring; otherwise goes straight to the
                 credit-card iframe. */}
            {!showIframe && (
              <button
                type="button"
                onClick={startDonate}
                disabled={donating}
                className="w-full flex items-center justify-center gap-2 rounded-full text-white font-bold text-base transition-all active:scale-[0.98] disabled:opacity-50"
                style={{
                  height: 56,
                  background: `linear-gradient(135deg, ${primary}, ${primary}DD)`,
                  boxShadow: `0 4px 12px ${primary}40`,
                }}
              >
                ❤️ תרום עכשיו
              </button>
            )}

            {/* External payment alongside the built-in form (mode='replace'
                reroutes the main button instead, inside startDonate). */}
            {!showIframe && campaign?.externalPayment?.provider && campaign?.externalPayment?.mode === 'alongside' && (
              <button
                type="button"
                onClick={() => {
                  if (campaign?.externalPayment?.provider === 'nedarim') {
                    const amount = getAmount();
                    if (!amount || amount <= 0) { setDonationError('אנא בחר סכום תרומה'); return; }
                    if (!donorName.trim()) { setDonationError('אנא הזן שם'); return; }
                    if (!termsAccepted) { setDonationError('יש לאשר את התקנון ומדיניות הפרטיות לפני ביצוע התשלום'); return; }
                    setDonationError(null);
                  }
                  openExternalPayment();
                }}
                className="w-full flex items-center justify-center gap-2 rounded-full font-bold text-sm transition-all active:scale-[0.98]"
                style={{ height: 48, background: 'transparent', color: primary, border: `2px solid ${primary}` }}
              >
                {campaign?.externalPayment?.provider === 'nedarim' ? 'תרומה דרך נדרים פלוס' : 'תרומה דרך jGive'}
              </button>
            )}

            {/* Trust row */}
            <div className="flex items-center justify-center gap-3 pt-1 flex-wrap">
              <div className="flex items-center gap-1 text-gray-400 text-xs"><span>🔒</span><span>SSL מאובטח</span></div>
              <div className="w-px h-3 bg-gray-200" />
              {campaign.zeroCommission && (
                <><div className="flex items-center gap-1 text-gray-400 text-xs"><span>💚</span><span>0% עמלה</span></div><div className="w-px h-3 bg-gray-200" /></>
              )}
              <div className="flex items-center gap-1 text-gray-400 text-xs"><span>🧾</span><span>קבלה מיידית</span></div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50" dir={outerDir} style={{ fontFamily: "'Heebo', sans-serif" }}>

      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100]">
          {['🎉','🎊','✨','💫','🌟','❤️','🙏','💛','🎈','🥳'].map((e, i) => (
            <span key={i} className="absolute confetti-piece" style={{
              left: `${Math.random() * 100}%`, top: '-50px',
              fontSize: `${Math.random() * 20 + 16}px`,
              animationDelay: `${Math.random() * 0.8}s`,
            }}>{e}</span>
          ))}
        </div>
      )}

      {/* Ambassador banner */}
      {resolvedAmbassador && (
        <div className="text-white text-center py-2.5 px-4 text-sm font-semibold"
          style={{ background: `linear-gradient(135deg, ${primary}, ${primary}CC)` }}>
          🤝 הגעת דרך <strong>{resolvedAmbassador.name}</strong> — תרומתך תזוכה לשגריר זה
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO — full-bleed, 70vh min — slideshow if gallery, else single
      ═══════════════════════════════════════════════════════════════════════ */}
      {(() => {
        const slides: string[] = [];
        // dispImageUrl falls back to campaign.imageUrl when imageUrlI18n is
        // unset (legacy campaigns), so this is byte-identical for them.
        if (dispImageUrl) slides.push(dispImageUrl);
        if (campaign.gallery?.length) {
          campaign.gallery.forEach(u => { if (u && !slides.includes(u)) slides.push(u); });
        }

        const heroInner = (
          <div className="flex flex-col items-center justify-end h-full px-5 pb-12 pt-24 text-center" style={{ minHeight: '70vh' }}>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-bold px-4 py-2 rounded-full mb-5 shadow-lg">
              <span className="text-base">🏛️</span>
              <span>{campaign.orgName}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight max-w-3xl"
              style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.3)' }}>
              {dispName || campaign.name}
            </h1>
            <div className="flex flex-wrap gap-2 justify-center">
              {campaign.zeroCommission && (
                <span className="bg-white/20 backdrop-blur text-white text-sm font-bold px-4 py-2 rounded-full border border-white/30">✅ 0% עמלה</span>
              )}
              {campaign.section46 && (
                <span className="bg-white/20 backdrop-blur text-white text-sm font-bold px-4 py-2 rounded-full border border-white/30">📋 סעיף 46</span>
              )}
              {daysLeft > 0 && daysLeft <= 7 && (
                <span className="bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-full animate-pulse shadow-lg shadow-red-500/50">⏰ נותרו {daysLeft} ימים!</span>
              )}
            </div>
            <div className={`animate-bounce ${slides.length > 1 ? 'mt-6 mb-8' : 'mt-10'}`}>
              <svg className="w-6 h-6 text-white/60 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        );

        if (slides.length > 1) {
          return (
            <div ref={heroRef}>
              <HeroSlideshow images={slides} minHeight="70vh">
                {heroInner}
              </HeroSlideshow>
            </div>
          );
        }

        return (
          <div ref={heroRef} className="relative w-full overflow-hidden" style={{ minHeight: '70vh' }}>
            {slides.length === 1
              ? <img src={slides[0]} alt={dispName || campaign.name} className="absolute inset-0 w-full h-full object-cover" />
              : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primary}, ${lightenHex(primary, 0.3)})` }} />
            }
            <div className="absolute inset-0 hero-shimmer" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.20) 40%, rgba(0,0,0,0.80) 100%)' }} />
            <div className="relative z-10">{heroInner}</div>
          </div>
        );
      })()}

      {/* Background music audio element */}
      {campaign.backgroundMusicUrl && (
        <audio ref={audioRef} src={campaign.backgroundMusicUrl} loop preload="none" />
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          RAISED AMOUNT — HERO of the page, first thing after hero image
      ═══════════════════════════════════════════════════════════════════════ */}
      {campaign.showProgress !== false && (
        <div className="bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-6xl mx-auto px-5 py-8">

            {/* Big raised amount */}
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-2 mb-1">
                <AnimatedNumber
                  value={campaign.raised}
                  prefix="₪"
                  className="font-black leading-none"
                  style={{ fontSize: 'clamp(3rem, 8vw, 5rem)', color: primary } as any}
                />
                {isTrending && (
                  <span className="inline-flex items-center gap-1 text-green-600 font-bold text-lg">
                    ↑ מגמה חיובית 🔥
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-base font-medium">
                גויס מתוך יעד של <span className="font-bold text-gray-600">₪{campaign.goal.toLocaleString('he-IL')}</span>
              </p>
            </div>

            {/* Progress bar — thick */}
            <div className="w-full bg-gray-100 rounded-full overflow-hidden mb-5" style={{ height: 24 }}>
              <div
                className="h-full rounded-full transition-all duration-[1800ms] ease-out relative overflow-hidden"
                style={{
                  width: `${progressWidth}%`,
                  background: `linear-gradient(90deg, ${primary}, ${primaryLight})`,
                }}
              >
                <div className="absolute inset-0 bg-white/20 animate-shimmer rounded-full" />
              </div>
            </div>

            {/* 3-stat chips */}
            <div className="flex justify-center gap-3 flex-wrap">
              {campaign.showDonorCount !== false && (
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm font-bold text-gray-700">
                  <span>👥</span>
                  <span>{donorCount.toLocaleString()} תורמים</span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm font-bold text-gray-700">
                <span>🎯</span>
                <span>₪{campaign.goal.toLocaleString()} יעד</span>
              </div>
              {daysLeft > 0 && (
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm font-bold text-gray-700">
                  <span>📅</span>
                  <span>{daysLeft} ימים</span>
                </div>
              )}
              {isTrending && (
                <div className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: '#fef9c3', color: '#854d0e' }}>
                  🔥 מגמה חיובית
                </div>
              )}
              {/* Desktop donate button in bar */}
              <button
                onClick={scrollToForm}
                className="hidden md:flex items-center gap-2 px-6 py-2 rounded-full text-white font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 text-sm"
                style={{ background: `linear-gradient(135deg, ${primary}, ${primaryLight})` }}
              >
                תרמו עכשיו ←
              </button>
            </div>

            {/* Countdown — right below stats */}
            {campaign.showCountdown !== false && campaign.endDate && new Date(campaign.endDate).getTime() > Date.now() && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-center text-sm text-gray-400 font-medium mb-4">⏱ הזמן שנותר לקמפיין</p>
                <Countdown endDate={campaign.endDate} primary={primary} />
              </div>
            )}
          </div>
        </div>
      )}



      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              LEFT COLUMN
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          <div className="flex-1 min-w-0 space-y-8 order-2 lg:order-1">

            {/* Countdown placeholder in sidebar — actual countdown is full-width below hero */}

            {/* Gallery */}
            {(campaign.gallery?.length || 0) > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-bold text-gray-800 text-lg mb-5 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm" style={{ background: primary }}>📸</span>
                  גלריה
                </h2>
                <GalleryCarousel images={campaign.gallery || []} mainImage={dispImageUrl || campaign.imageUrl} />
              </div>
            )}

            {/* Video */}
            {campaign.videoUrl && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-bold text-gray-800 text-lg mb-5 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm" style={{ background: primary }}>🎬</span>
                  וידאו
                </h2>
                <VideoEmbed url={campaign.videoUrl} />
              </div>
            )}

            {/* Description */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="font-bold text-gray-800 text-lg mb-5 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm" style={{ background: primary }}>📖</span>
                על הקמפיין
              </h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line text-base">
                {dispAiDescription || dispDescription || campaign.aiDescription || campaign.description}
              </p>
            </div>

            {/* Mobile: donation form inline */}
            <div className="lg:hidden" id="mobile-form">
              <DonationForm />
            </div>

            {/* Donors Feed */}
            {campaign.showDonationsFeed !== false && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm" style={{ background: primary }}>❤️</span>
                    הצטרפו אלינו
                  </h2>
                  {campaign.showDonorCount !== false && (
                    <span className="text-sm font-bold px-3 py-1 rounded-full text-white" style={{ background: primary }}>
                      {donorCount.toLocaleString()} תורמים
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {visibleDonors.map((donor, i) => (
                    <DonorCard key={i} donor={donor} primary={primary} index={i} />
                  ))}
                </div>
                {FAKE_DONORS.length > 6 && (
                  <button
                    onClick={() => setShowAllDonors(v => !v)}
                    className="mt-4 w-full py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
                  >
                    {showAllDonors ? '▲ הצג פחות' : `▼ הצג עוד ${FAKE_DONORS.length - 6} תורמים`}
                  </button>
                )}
              </div>
            )}

            {/* Share */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="font-bold text-gray-800 text-lg mb-2">📣 שתפו את הקמפיין</h2>
              <p className="text-gray-500 text-sm mb-5">כל שיתוף מגדיל את הסיכוי להגיע ליעד 🚀</p>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-bold text-sm shadow-sm hover:brightness-110 transition-all"
                  style={{ background: '#25D366' }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  שתף בוואטסאפ
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
                >
                  {copied ? '✅ הועתק!' : '🔗 העתק קישור'}
                </button>
              </div>
            </div>

          </div>{/* end left column */}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              RIGHT COLUMN — Sticky donation form (desktop only)
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          <div className="hidden lg:block w-[420px] flex-shrink-0 order-1 lg:order-2">
            <div className="sticky top-6">
              <DonationForm />
            </div>
          </div>

        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════════════════════════════════ */}
      <footer className="mt-8 bg-white border-t border-gray-100 py-8 pb-28">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <p className="text-gray-400 text-sm">
            קמפיין של <strong className="text-gray-600">{campaign.orgName}</strong>
            {' '}— מופעל ע"י <strong className="text-gray-600">Amutech</strong>
          </p>
          {(campaign.zeroCommission || campaign.section46) && (
            <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
              {campaign.zeroCommission && <span className="text-xs text-gray-400">✅ 100% מהתרומה מגיע לעמותה</span>}
              {campaign.section46 && <span className="text-xs text-gray-400">📋 תרומה מוכרת לצרכי מס — סעיף 46</span>}
              <span className="text-xs text-gray-400">🔒 תשלום מאובטח SSL</span>
            </div>
          )}
        </div>
      </footer>

      {/* ═══════════════════════════════════════════════════════════════════════
          FLOATING MUSIC BUTTON (above sticky bar)
      ═══════════════════════════════════════════════════════════════════════ */}
      {campaign.backgroundMusicUrl && (
        <button
          onClick={toggleMusic}
          title={musicPlaying ? 'הפסק מוסיקה' : 'לחץ להפעלת מוסיקה'}
          className="fixed z-[60] flex items-center justify-center rounded-full text-white text-xl transition-all hover:scale-110 active:scale-95"
          style={{
            width: 48,
            height: 48,
            bottom: showStickyBar ? 84 : 24,
            right: 16,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(8px)',
            boxShadow: musicPlaying ? '0 0 0 3px rgba(255,255,255,0.3), 0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.35)',
            transition: 'bottom 0.3s ease, box-shadow 0.2s ease',
          }}
          aria-label={musicPlaying ? 'הפסק מוסיקה' : 'הפעל מוסיקה'}
        >
          {musicPlaying ? '🔊' : '🎵'}
        </button>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          CHARIDY-STYLE STICKY BOTTOM BAR — always visible on scroll
      ═══════════════════════════════════════════════════════════════════════ */}

      {/* 🎵 Music button */}
      {campaign.backgroundMusicUrl && (
        <button
          onClick={() => {
            if (audioRef.current) {
              if (musicPlaying) {
                audioRef.current.pause();
                setMusicPlaying(false);
              } else {
                audioRef.current.volume = 0.4;
                audioRef.current.play().then(() => setMusicPlaying(true)).catch(() => {});
              }
            }
          }}
          className="fixed z-50 flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all hover:scale-110"
          style={{
            bottom: showStickyBar ? 88 : 24,
            left: 16,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(8px)',
          }}
          title={musicPlaying ? 'הפסק מוסיקה' : 'לחץ להפעלת מוסיקה'}
        >
          <span className="text-xl">{musicPlaying ? '🔊' : '🎵'}</span>
        </button>
      )}

      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl transition-transform duration-300"
        style={{
          transform: showStickyBar ? 'translateY(0)' : 'translateY(100%)',
          height: 72,
        }}
      >
        <div className="max-w-2xl mx-auto px-4 h-full flex items-center gap-3">

          {/* Share button */}
          <button
            onClick={handleShare}
            className="flex-1 flex items-center justify-center gap-2 border-2 border-gray-300 rounded-full text-gray-700 font-bold text-sm transition-all hover:bg-gray-50 active:scale-[0.97]"
            style={{ height: 48 }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span>שתף</span>
          </button>

          {/* Donate button */}
          <button
            onClick={scrollToForm}
            className="flex-[2] flex items-center justify-center gap-2 rounded-full text-white font-black text-base transition-all hover:brightness-110 active:scale-[0.97] shadow-lg"
            style={{
              height: 52,
              background: `linear-gradient(135deg, #E53E3E, #FC8181)`,
              boxShadow: '0 4px 20px rgba(229, 62, 62, 0.45)',
            }}
          >
            <span>תרום עכשיו ←</span>
          </button>

          {/* Bit button — hidden for non-ILS currencies because Bit/GamaPay
              doesn't support foreign currency. Also hidden when the donor
              toggled a recurring/standing-order donation: Bit has no stored
              token, so monthly charges can't run through it. The card button
              still works. */}
          {currency === 'ILS' && !isRecurring && (
            <button
              onClick={() => {
                scrollToForm();
                setTimeout(() => handleDonate('bit'), 300);
              }}
              className="flex-1 flex items-center justify-center gap-2 rounded-full text-white font-bold text-sm transition-all hover:brightness-110 active:scale-[0.97] shadow-md"
              style={{ height: 48, background: '#00ADA8' }}
            >
              <BitLogo size={20} />
              <span>bit</span>
            </button>
          )}

        </div>
      </div>

      {/* Spacer so content isn't hidden behind sticky bar */}
      <div className="h-20" />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');

        @keyframes confetti-fall {
          0%   { transform: translateY(-60px) rotate(0deg) scale(1); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(100vh) rotate(540deg) scale(0.5); opacity: 0; }
        }
        .confetti-piece {
          position: absolute;
          animation: confetti-fall 2.8s ease-in forwards;
        }

        @keyframes pulse-border {
          0%, 100% { box-shadow: 0 0 0 3px rgba(var(--primary-rgb, 21,101,192), 0.15); }
          50%       { box-shadow: 0 0 0 6px rgba(var(--primary-rgb, 21,101,192), 0.30); }
        }

        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        @keyframes hero-shimmer-anim {
          0%   { opacity: 0; transform: translateX(-100%) skewX(-20deg); }
          50%  { opacity: 0.06; }
          100% { opacity: 0; transform: translateX(200%) skewX(-20deg); }
        }
        .hero-shimmer::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          animation: hero-shimmer-anim 4s ease-in-out infinite;
          pointer-events: none;
        }

        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Sticky Header */}
      <PublicHeader />

      {/* Bilingual language toggle — ONLY rendered for campaigns whose owner
          has explicitly enabled English (bilingualEnabled + 'en' in
          supportedLanguages). Every legacy campaign has bilingualEnabled
          undefined/false → this is invisible and the page is byte-identical
          to before this change. Clicking either button navigates to
          ?lang=he|en which triggers a full reload, so the Emotion cache +
          theme rebuild cleanly at the new direction (see main.tsx).

          Position: floating just under the sticky PublicHeader (top:92 on
          desktop, top:84 on mobile-ish). Sized + styled like a primary CTA
          so donors actually notice it. */}
      {campaign?.bilingualEnabled && (campaign.supportedLanguages || []).includes('en') && (
        <div
          dir="ltr"
          style={{
            position: 'fixed',
            top: 92,
            insetInlineEnd: 16,
            zIndex: 9500,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 6px 6px 12px',
            borderRadius: 999,
            background: '#fff',
            boxShadow: '0 6px 24px rgba(124,58,237,0.18), 0 1px 4px rgba(0,0,0,0.06)',
            border: '2px solid #7c3aed',
            fontFamily: "'Heebo', sans-serif",
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>🌐</span>
          {(['he', 'en'] as const).map((lng) => {
            const active = getBootLocale() === lng;
            return (
              <a
                key={lng}
                href={buildLocaleUrl(lng)}
                aria-current={active ? 'true' : undefined}
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 800,
                  textDecoration: 'none',
                  color: active ? '#fff' : '#5b21b6',
                  background: active ? '#7c3aed' : 'transparent',
                  border: active ? 'none' : '1px solid transparent',
                  transition: 'all 0.15s',
                  cursor: active ? 'default' : 'pointer',
                  lineHeight: 1,
                }}
              >
                {lng === 'he' ? 'עברית' : 'EN'}
              </a>
            );
          })}
        </div>
      )}

      <PaymentIframeModal
        url={paymentUrl}
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        onFail={handlePaymentFail}
        bitContext={campaign?.orgId ? {
          orgId: campaign.orgId,
          amount: getAmount(),
          campaignId: campaign?._id || undefined,
          ambassadorSlug: resolvedAmbassador?.slug || undefined,
          donorName: donorName || undefined,
          donorEmail: donorEmail || undefined,
          // PublicCampaign main flow doesn't currently capture phone — BIT
          // requires it, so this surface will only show the BIT button if
          // a phone is collected (the campaign builder flow does collect it).
          donorPhone: undefined,
        } : undefined}
      />

      {/* External payment surfaces — נדרים פלוס / jGive (per-campaign).
          Nedarim opens its hosted online page prefilled via the URL; the
          webhook records the donation. */}
      <ExternalIframeModal
        open={nedarimModalOpen}
        url={campaign?.nedarimIframe?.mosadId ? buildNedarimOnlineUrl(campaign!.nedarimIframe!.mosadId, {
          groupe: campaign?.nedarimGroupe || campaign?.name || campaign?.slug || '',
          currency: currency === 'USD' ? 'USD' : 'ILS',
          amount: getAmount(),
          recurring: isRecurring,
          recurringMonths,
          clientName: joinDonorFullName(donorName, ''),
          email: donorEmail,
          param1: campaign?._id ? `campaign:${campaign?._id}` : '',
          param2: ambassadorRef ? `ambassador:${ambassadorRef}` : '',
        }) : ''}
        title="תרומה דרך נדרים פלוס"
        onClose={() => setNedarimModalOpen(false)}
      />
      <ExternalIframeModal
        open={externalJgiveOpen}
        url={campaign?.externalPayment?.jgiveUrl || ''}
        title="תרומה דרך jGive"
        onClose={() => setExternalJgiveOpen(false)}
      />

      {/* Method chooser — shown only when the org has Bit configured and
          the donor isn't on recurring. Two options: Bit or credit card. */}
      <PaymentMethodDialog
        open={methodChoiceOpen}
        onClose={() => setMethodChoiceOpen(false)}
        onSelect={(m) => {
          setMethodChoiceOpen(false);
          handleDonate(m === 'bit' ? 'bit' : 'card');
        }}
        title="בחר אמצעי תשלום"
        description={`תרומה בסך ₪${getAmount().toLocaleString('he-IL')}`}
      />

      {/* Bit donation modal — opens when the donor picks "Bit" in the
          chooser. Routes to /payments/bit/* (donations), which credits the
          org's own GamaPay account. */}
      <BitPurchaseModal
        open={bitModalOpen}
        onClose={() => setBitModalOpen(false)}
        title="תשלום ב-Bit"
        initiateBody={bitInitiateBody}
        initiateUrl="/payments/bit/initiate"
        statusUrl="/payments/bit/status/"
        phoneFieldName="donorPhone"
        showIdField
        idFieldName="donorIdNumber"
        onSuccess={(data) => {
          setBitModalOpen(false);
          const qs = new URLSearchParams({
            method: 'bit',
            campaignSlug: campaign?.slug || '',
            ...(data?.transactionId ? { tranid: String(data.transactionId) } : {}),
          }).toString();
          window.location.href = `/payments/complete?${qs}`;
        }}
      />
    </div>
  );
};

export default PublicCampaign;
